/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import logo from './logo.png';

// ============================================================
// CONFIG
// ============================================================
const BASE = 'https://essencevedacrm.online/api';

// ============================================================
// API
// ============================================================
const api = async (endpoint, method = 'GET', body = null) => {
  const token = localStorage.getItem('ev_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  const opts = { method, headers };
  if (body) opts.body = JSON.stringify(body);
  try {
    const res = await fetch(`${BASE}/${endpoint}`, opts);
    return await res.json();
  } catch (e) {
    return { status: 'error', message: 'Network error. Check your connection.' };
  }
};

// ============================================================
// TOAST
// ============================================================
let toastTimer;
const toast = (msg, type = 'success') => {
  const el = document.getElementById('_toast');
  if (!el) return;
  el.textContent = msg;
  el.className = `_toast _toast-${type} _toast-show`;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { el.className = '_toast'; }, 3500);
};

// ============================================================
// THEME & STYLES
// ============================================================
const injectStyles = (dark) => {
  const id = '_crm_styles';
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('style'); el.id = id; document.head.appendChild(el); }
  el.textContent = `
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
    :root{
      --bg: ${dark ? '#0a1f0f' : '#f0f7f0'};
      --bg2: ${dark ? '#0f2915' : '#e8f5e8'};
      --card: ${dark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.85)'};
      --card-border: ${dark ? 'rgba(255,255,255,0.1)' : 'rgba(45,106,79,0.15)'};
      --sidebar: ${dark ? '#051209' : '#1b4332'};
      --text: ${dark ? '#e8f5e8' : '#1a2e1f'};
      --text2: ${dark ? '#8fbc8f' : '#4a7c59'};
      --text3: ${dark ? '#5a8a5a' : '#7a9a7a'};
      --green: #00c853;
      --green2: #69f0ae;
      --orange: #ff6b2b;
      --orange2: #ff9a3c;
      --red: #f44336;
      --yellow: #ffc107;
      --blue: #2196f3;
      --purple: #9c27b0;
      --shadow: ${dark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.12)'};
      --shadow2: ${dark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)'};
      --blur: blur(20px);
      --r: 14px;
      --r-sm: 8px;
      --r-lg: 20px;
      --font: 'Inter',sans-serif;
      --font-h: 'Poppins',sans-serif;
      --mono: 'JetBrains Mono',monospace;
    }
    body{background:var(--bg);color:var(--text);font-family:var(--font);font-size:14px;min-height:100vh;-webkit-font-smoothing:antialiased;transition:background .3s}
    body::before{content:'';position:fixed;inset:0;background:${dark ? 'radial-gradient(ellipse at top left,rgba(0,200,83,0.08) 0%,transparent 60%),radial-gradient(ellipse at bottom right,rgba(255,107,43,0.06) 0%,transparent 60%)' : 'radial-gradient(ellipse at top left,rgba(0,200,83,0.06) 0%,transparent 60%),radial-gradient(ellipse at bottom right,rgba(255,107,43,0.04) 0%,transparent 60%)'};pointer-events:none;z-index:0}
    ::-webkit-scrollbar{width:4px;height:4px}
    ::-webkit-scrollbar-thumb{background:var(--text3);border-radius:4px}
    button{cursor:pointer;font-family:var(--font);border:none;outline:none}
    input,select,textarea{font-family:var(--font);outline:none}
    
    /* LAYOUT */
    .layout{display:flex;min-height:100vh;position:relative;z-index:1}
    
    /* SIDEBAR */
    .sidebar{width:260px;background:var(--sidebar);height:100vh;position:fixed;left:0;top:0;display:flex;flex-direction:column;z-index:100;box-shadow:4px 0 30px rgba(0,0,0,.3);overflow:hidden}
    .sidebar::before{content:'';position:absolute;top:-50px;right:-50px;width:200px;height:200px;border-radius:50%;background:rgba(0,200,83,0.06);pointer-events:none}
    .sb-logo{padding:20px 18px 16px;border-bottom:1px solid rgba(255,255,255,.08);display:flex;align-items:center;gap:12px}
    .sb-logo img{width:46px;height:46px;object-fit:contain;border-radius:10px;background:#fff;padding:4px}
    .sb-brand{flex:1}
    .sb-brand-name{color:#fff;font-family:var(--font-h);font-weight:700;font-size:14px;line-height:1.2}
    .sb-brand-sub{color:rgba(255,255,255,.4);font-size:10px;text-transform:uppercase;letter-spacing:1px;margin-top:2px}
    .sb-role-badge{margin:10px 14px;background:linear-gradient(135deg,var(--orange),var(--orange2));border-radius:20px;padding:5px 14px;color:#fff;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;text-align:center;box-shadow:0 4px 15px rgba(255,107,43,.3)}
    .sb-nav{flex:1;padding:8px 10px;overflow-y:auto}
    .nav-item{display:flex;align-items:center;gap:10px;padding:11px 13px;border-radius:var(--r-sm);background:transparent;color:rgba(255,255,255,.5);font-size:13px;font-weight:500;width:100%;text-align:left;transition:all .2s;margin-bottom:2px;position:relative}
    .nav-item svg{width:17px;height:17px;flex-shrink:0;opacity:.7}
    .nav-item:hover{background:rgba(255,255,255,.07);color:rgba(255,255,255,.85)}
    .nav-item.active{background:linear-gradient(135deg,rgba(0,200,83,.2),rgba(105,240,174,.1));color:#fff;font-weight:600;border:1px solid rgba(0,200,83,.2)}
    .nav-item.active svg{opacity:1}
    .nav-badge-count{margin-left:auto;background:var(--red);color:#fff;border-radius:10px;padding:2px 7px;font-size:10px;font-weight:700}
    .sb-footer{padding:14px;border-top:1px solid rgba(255,255,255,.08)}
    .sb-user{display:flex;align-items:center;gap:10px;padding:10px 12px;background:rgba(255,255,255,.06);border-radius:var(--r-sm);margin-bottom:10px}
    .sb-avatar{width:36px;height:36px;border-radius:50%;background:linear-gradient(135deg,var(--orange),var(--orange2));color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:15px;flex-shrink:0}
    .sb-uname{color:#fff;font-size:13px;font-weight:600}
    .sb-urole{color:rgba(255,255,255,.4);font-size:10px;margin-top:1px;text-transform:capitalize}
    .logout-btn{width:100%;padding:9px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:var(--r-sm);color:rgba(255,255,255,.5);font-size:12px;font-weight:600;transition:all .2s}
    .logout-btn:hover{background:rgba(244,67,54,.2);border-color:rgba(244,67,54,.4);color:#fff}
    
    /* MAIN */
    .main{margin-left:260px;flex:1;display:flex;flex-direction:column;min-height:100vh}
    .topbar{background:var(--card);backdrop-filter:var(--blur);border-bottom:1px solid var(--card-border);padding:14px 28px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:50;box-shadow:var(--shadow2)}
    .topbar-title{font-family:var(--font-h);font-size:19px;font-weight:700;color:var(--text);background:linear-gradient(135deg,var(--green),var(--green2));-webkit-background-clip:text;-webkit-text-fill-color:transparent}
    .topbar-sub{color:var(--text3);font-size:12px;margin-top:2px}
    .topbar-right{display:flex;align-items:center;gap:12px}
    .page{padding:24px 28px;flex:1}
    
    /* CARDS */
    .card{background:var(--card);backdrop-filter:var(--blur);border:1px solid var(--card-border);border-radius:var(--r);padding:22px;margin-bottom:20px;box-shadow:var(--shadow2);transition:box-shadow .2s}
    .card:hover{box-shadow:var(--shadow)}
    .card-title{font-family:var(--font-h);font-size:15px;font-weight:700;color:var(--text);margin-bottom:18px;display:flex;align-items:center;gap:8px;padding-bottom:12px;border-bottom:1px solid var(--card-border)}
    .card-title-icon{width:32px;height:32px;border-radius:8px;background:linear-gradient(135deg,var(--green),var(--green2));display:flex;align-items:center;justify-content:center;color:#fff;font-size:15px}
    
    /* STAT CARDS */
    .stat-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:16px;margin-bottom:20px}
    .stat-grid-3{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-bottom:20px}
    .stat-card{background:var(--card);backdrop-filter:var(--blur);border:1px solid var(--card-border);border-radius:var(--r);padding:20px;position:relative;overflow:hidden;transition:all .2s;cursor:default}
    .stat-card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:var(--ac,linear-gradient(90deg,var(--green),var(--green2)))}
    .stat-card:hover{transform:translateY(-2px);box-shadow:var(--shadow)}
    .stat-icon{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:20px;margin-bottom:14px}
    .stat-val{font-family:var(--font-h);font-size:30px;font-weight:800;color:var(--text);line-height:1;margin-bottom:5px}
    .stat-label{color:var(--text3);font-size:12px;font-weight:500}
    
    /* GRIDS */
    .g2{display:grid;grid-template-columns:1fr 1fr;gap:20px}
    .g3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px}
    
    /* FORM */
    .field{margin-bottom:16px}
    .flabel{display:block;color:var(--text2);font-size:11px;font-weight:700;margin-bottom:6px;text-transform:uppercase;letter-spacing:.5px}
    .inp{width:100%;background:${dark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.9)'};border:1.5px solid var(--card-border);border-radius:var(--r-sm);padding:10px 13px;color:var(--text);font-size:13px;transition:all .2s}
    .inp:focus{border-color:var(--green);box-shadow:0 0 0 3px rgba(0,200,83,.15)}
    .inp::placeholder{color:var(--text3)}
    .sel{width:100%;background:${dark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.9)'};border:1.5px solid var(--card-border);border-radius:var(--r-sm);padding:10px 13px;color:var(--text);font-size:13px;transition:all .2s;cursor:pointer}
    .sel:focus{border-color:var(--green);box-shadow:0 0 0 3px rgba(0,200,83,.15)}
    .ta{width:100%;background:${dark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.9)'};border:1.5px solid var(--card-border);border-radius:var(--r-sm);padding:10px 13px;color:var(--text);font-size:13px;resize:vertical;min-height:80px;transition:all .2s}
    .ta:focus{border-color:var(--green);box-shadow:0 0 0 3px rgba(0,200,83,.15)}
    
    /* BUTTONS */
    .btn{border:none;border-radius:var(--r-sm);padding:10px 20px;font-size:13px;font-weight:600;display:inline-flex;align-items:center;gap:7px;transition:all .2s;white-space:nowrap;cursor:pointer}
    .btn:disabled{opacity:.4;cursor:not-allowed}
    .btn-full{width:100%;justify-content:center;padding:12px 20px}
    .btn-sm{padding:7px 13px;font-size:12px;border-radius:6px}
    .btn-green{background:linear-gradient(135deg,var(--green),#00a843);color:#fff;box-shadow:0 4px 15px rgba(0,200,83,.3)}
    .btn-green:not(:disabled):hover{box-shadow:0 6px 20px rgba(0,200,83,.5);transform:translateY(-1px)}
    .btn-orange{background:linear-gradient(135deg,var(--orange),var(--orange2));color:#fff;box-shadow:0 4px 15px rgba(255,107,43,.3)}
    .btn-orange:not(:disabled):hover{box-shadow:0 6px 20px rgba(255,107,43,.5);transform:translateY(-1px)}
    .btn-red{background:linear-gradient(135deg,#f44336,#e53935);color:#fff;box-shadow:0 4px 15px rgba(244,67,54,.3)}
    .btn-red:not(:disabled):hover{transform:translateY(-1px)}
    .btn-ghost{background:${dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'};border:1.5px solid var(--card-border);color:var(--text2)}
    .btn-ghost:not(:disabled):hover{border-color:var(--green);color:var(--green);background:rgba(0,200,83,.08)}
    .btn-blue{background:linear-gradient(135deg,#2196f3,#1976d2);color:#fff}
    
    /* BADGES */
    .badge{display:inline-flex;align-items:center;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:600}
    .bg-green{background:rgba(0,200,83,.15);color:var(--green)}
    .bg-orange{background:rgba(255,107,43,.15);color:var(--orange)}
    .bg-red{background:rgba(244,67,54,.15);color:var(--red)}
    .bg-blue{background:rgba(33,150,243,.15);color:var(--blue)}
    .bg-gray{background:${dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};color:var(--text3)}
    .bg-yellow{background:rgba(255,193,7,.15);color:var(--yellow)}
    .bg-purple{background:rgba(156,39,176,.15);color:var(--purple)}
    
    /* TABLE */
    .tbl{width:100%;border-collapse:collapse}
    .tbl th{color:var(--text3);font-size:11px;font-weight:700;text-align:left;padding:10px 14px;border-bottom:2px solid var(--card-border);text-transform:uppercase;letter-spacing:.5px;white-space:nowrap;background:${dark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)'}}
    .tbl td{padding:12px 14px;border-bottom:1px solid var(--card-border);font-size:13px;color:var(--text);vertical-align:middle}
    .tbl tr:last-child td{border-bottom:none}
    .tbl tr:hover td{background:${dark ? 'rgba(0,200,83,0.04)' : 'rgba(0,200,83,0.03)'}}
    .tbl-row-green td{background:rgba(0,200,83,.05) !important}
    .tbl-row-yellow td{background:rgba(255,193,7,.05) !important}
    .tbl-row-red td{background:rgba(244,67,54,.05) !important}
    
    /* TABS */
    .tabs{display:flex;gap:3px;background:${dark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.06)'};padding:4px;border-radius:var(--r);margin-bottom:20px}
    .tab{flex:1;padding:9px 16px;border-radius:var(--r-sm);background:transparent;color:var(--text3);font-size:13px;font-weight:600;transition:all .2s;border:none;cursor:pointer}
    .tab.active{background:var(--card);color:var(--text);box-shadow:var(--shadow2)}
    
    /* SEARCH BAR */
    .search-bar{display:flex;align-items:center;gap:10px;background:var(--card);backdrop-filter:var(--blur);border:1.5px solid var(--card-border);border-radius:var(--r);padding:10px 16px;margin-bottom:16px}
    .search-bar input{flex:1;border:none;background:transparent;color:var(--text);font-size:14px}
    .search-bar input::placeholder{color:var(--text3)}
    
    /* MODAL */
    .modal-bg{display:none;position:fixed;inset:0;background:rgba(0,0,0,.6);backdrop-filter:blur(6px);z-index:1000;align-items:center;justify-content:center}
    .modal-bg.open{display:flex}
    .modal{background:${dark ? 'rgba(10,31,15,0.98)' : 'rgba(255,255,255,0.98)'};backdrop-filter:var(--blur);border:1px solid var(--card-border);border-radius:var(--r-lg);padding:28px;width:520px;max-width:95vw;max-height:92vh;overflow-y:auto;box-shadow:var(--shadow)}
    .modal-title{font-family:var(--font-h);font-size:18px;font-weight:700;color:var(--text);margin-bottom:22px;padding-bottom:14px;border-bottom:1px solid var(--card-border)}
    
    /* TOAST */
    ._toast{position:fixed;bottom:24px;right:24px;padding:14px 22px;border-radius:var(--r);font-size:13px;font-weight:600;z-index:9999;opacity:0;transform:translateY(10px);transition:all .3s;pointer-events:none;max-width:350px;box-shadow:var(--shadow)}
    ._toast-show{opacity:1;transform:translateY(0)}
    ._toast-success{background:linear-gradient(135deg,var(--green),#00a843);color:#fff}
    ._toast-error{background:linear-gradient(135deg,#f44336,#e53935);color:#fff}
    ._toast-info{background:linear-gradient(135deg,#2196f3,#1976d2);color:#fff}
    ._toast-warning{background:linear-gradient(135deg,var(--orange),var(--orange2));color:#fff}
    
    /* PROGRESS */
    .progress-wrap{background:${dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};border-radius:8px;height:10px;overflow:hidden}
    .progress-bar{height:100%;border-radius:8px;transition:width .6s ease;background:linear-gradient(90deg,var(--green),var(--green2))}
    
    /* LEAD CARD */
    .lead-hero{background:linear-gradient(135deg,#0a3d1f,#1a6b3a);border-radius:var(--r-lg);padding:26px;color:#fff;margin-bottom:20px;position:relative;overflow:hidden;box-shadow:0 10px 40px rgba(0,100,0,.4)}
    .lead-hero::before{content:'';position:absolute;top:-40px;right:-40px;width:180px;height:180px;border-radius:50%;background:rgba(0,200,83,.1)}
    .lead-hero::after{content:'';position:absolute;bottom:-30px;left:-30px;width:130px;height:130px;border-radius:50%;background:rgba(255,107,43,.08)}
    .lead-hero.priority{background:linear-gradient(135deg,#3d1a0a,#6b3a1a)}
    .lead-phone{font-family:var(--font-h);font-size:32px;font-weight:800;letter-spacing:3px;margin:10px 0 6px}
    .lead-name{font-size:18px;font-weight:600;opacity:.9}
    .lead-product{font-size:11px;opacity:.6;text-transform:uppercase;letter-spacing:.8px}
    .lead-pending{position:absolute;top:22px;right:22px;background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.25);border-radius:20px;padding:5px 14px;font-size:12px;font-weight:700}
    
    /* DISPOSITION GRID */
    .disp-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:18px}
    .disp-btn{padding:12px 8px;border-radius:var(--r-sm);border:2px solid var(--card-border);background:${dark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.7)'};color:var(--text2);font-size:12px;font-weight:600;text-align:center;transition:all .2s;cursor:pointer}
    .disp-btn:hover{border-color:var(--green);color:var(--green);background:rgba(0,200,83,.08)}
    .disp-btn.dsel-green{border-color:var(--green);background:rgba(0,200,83,.15);color:var(--green)}
    .disp-btn.dsel-orange{border-color:var(--orange);background:rgba(255,107,43,.15);color:var(--orange)}
    .disp-btn.dsel-red{border-color:var(--red);background:rgba(244,67,54,.15);color:var(--red)}
    
    /* ACCORDION */
    .accordion-item{border:1px solid var(--card-border);border-radius:var(--r);margin-bottom:10px;overflow:hidden}
    .accordion-header{padding:14px 18px;background:var(--card);backdrop-filter:var(--blur);display:flex;align-items:center;gap:12px;cursor:pointer;transition:background .2s}
    .accordion-header:hover{background:${dark ? 'rgba(0,200,83,0.06)' : 'rgba(0,200,83,0.04)'}}
    .accordion-body{padding:0 18px 14px;background:${dark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)'}}
    
    /* FU CARD */
    .fu-card{padding:14px 18px;border-radius:var(--r);border:1.5px solid var(--card-border);margin-bottom:10px;background:var(--card);backdrop-filter:var(--blur);transition:all .2s}
    .fu-card.overdue{border-color:var(--red);background:rgba(244,67,54,.05)}
    .fu-card.soon{border-color:var(--orange);background:rgba(255,107,43,.05)}
    
    /* MSG */
    .msg-item{padding:14px 18px;border-radius:var(--r);margin-bottom:8px;border:1px solid var(--card-border);background:var(--card);backdrop-filter:var(--blur);transition:all .2s}
    .msg-item.unread{border-color:var(--green);background:rgba(0,200,83,.06)}
    
    /* STATUS DOT */
    .dot{width:8px;height:8px;border-radius:50%;display:inline-block;flex-shrink:0}
    .dot-green{background:var(--green)}
    .dot-yellow{background:var(--yellow)}
    .dot-red{background:var(--red)}
    .dot-gray{background:var(--text3)}
    
    /* SPINNER */
    .spin{width:30px;height:30px;border:3px solid var(--card-border);border-top-color:var(--green);border-radius:50%;animation:_spin .7s linear infinite;display:inline-block}
    @keyframes _spin{to{transform:rotate(360deg)}}
    .center-spin{display:flex;align-items:center;justify-content:center;padding:60px}
    
    /* NOTIFICATION BELL */
    .notif-bell{position:relative;cursor:pointer;width:38px;height:38px;border-radius:var(--r-sm);background:var(--card);border:1px solid var(--card-border);display:flex;align-items:center;justify-content:center;color:var(--text2);transition:all .2s}
    .notif-bell:hover{border-color:var(--green);color:var(--green)}
    .notif-dot{position:absolute;top:6px;right:6px;width:8px;height:8px;border-radius:50%;background:var(--red);border:2px solid var(--bg)}
    
    /* THEME TOGGLE */
    .theme-btn{width:38px;height:38px;border-radius:var(--r-sm);background:var(--card);border:1px solid var(--card-border);display:flex;align-items:center;justify-content:center;color:var(--text2);font-size:16px;transition:all .2s;cursor:pointer}
    .theme-btn:hover{border-color:var(--orange);color:var(--orange)}
    
    /* SCRIPT PANEL */
    .script{background:${dark ? 'rgba(0,0,0,0.3)' : 'rgba(0,200,83,0.05)'};border:1px solid rgba(0,200,83,.2);border-radius:var(--r-sm);padding:16px;font-size:13px;line-height:1.8;color:var(--text);white-space:pre-wrap;max-height:200px;overflow-y:auto;font-family:var(--mono)}
    
    /* APPROVAL POPUP */
    .approval-popup{position:fixed;top:20px;right:20px;background:${dark ? 'rgba(10,31,15,0.98)' : '#fff'};border:1.5px solid var(--green);border-radius:var(--r);padding:18px;z-index:999;max-width:320px;box-shadow:0 10px 40px rgba(0,200,83,.2);animation:_slide-in .3s ease}
    @keyframes _slide-in{from{transform:translateX(100%);opacity:0}to{transform:translateX(0);opacity:1}}
    
    /* LOGIN */
    .login-wrap{min-height:100vh;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#051209 0%,#0a2010 40%,#051209 100%);position:relative;overflow:hidden}
    .login-wrap::before{content:'';position:absolute;top:-100px;left:-100px;width:400px;height:400px;border-radius:50%;background:radial-gradient(circle,rgba(0,200,83,0.12) 0%,transparent 70%)}
    .login-wrap::after{content:'';position:absolute;bottom:-100px;right:-100px;width:350px;height:350px;border-radius:50%;background:radial-gradient(circle,rgba(255,107,43,0.08) 0%,transparent 70%)}
    .login-card{background:rgba(255,255,255,0.04);backdrop-filter:blur(30px);border:1px solid rgba(255,255,255,0.1);border-radius:24px;padding:44px;width:420px;max-width:95vw;box-shadow:0 20px 60px rgba(0,0,0,0.5);position:relative;z-index:1}
    
    /* MISC */
    .divider{border:none;border-top:1px solid var(--card-border);margin:18px 0}
    .empty{text-align:center;padding:48px 20px;color:var(--text3)}
    .empty-icon{font-size:44px;opacity:.4;margin-bottom:14px}
    .chip{display:inline-flex;align-items:center;gap:4px;padding:4px 10px;border-radius:6px;font-size:11px;font-weight:600;background:var(--card);border:1px solid var(--card-border);color:var(--text2)}
    
    /* HISTORY ITEM */
    .hist-item{display:flex;gap:12px;padding:10px 0;border-bottom:1px solid var(--card-border);align-items:flex-start}
    .hist-item:last-child{border-bottom:none}
    .hist-time{color:var(--text3);font-size:11px;min-width:130px;padding-top:2px;font-family:var(--mono)}
    
    /* LEADERBOARD */
    .lb-row{display:flex;align-items:center;gap:14px;padding:14px 18px;border-radius:var(--r);margin-bottom:8px;background:var(--card);border:1px solid var(--card-border);transition:all .2s}
    .lb-row:hover{border-color:var(--green);box-shadow:var(--shadow2)}
    .lb-row.lb-me{background:rgba(0,200,83,.08);border-color:var(--green)}
    
    @media(max-width:768px){
      .sidebar{width:100%;height:auto;position:relative}
      .main{margin-left:0}
      .stat-grid{grid-template-columns:1fr 1fr}
      .g2,.g3{grid-template-columns:1fr}
      .page{padding:16px}
      .disp-grid{grid-template-columns:repeat(2,1fr)}
    }
  `;
};

// ============================================================
// UI COMPONENTS
// ============================================================
const Spin = () => <div className="center-spin"><div className="spin"/></div>;
const Empty = ({ title = 'No data found', desc = '' }) => (
  <div className="empty"><div className="empty-icon">📭</div><div style={{fontSize:15,fontWeight:600,marginBottom:6}}>{title}</div>{desc&&<div style={{fontSize:13}}>{desc}</div>}</div>
);
const Badge = ({ color='gray', children }) => <span className={`badge bg-${color}`}>{children}</span>;
const Btn = ({ children, color='green', onClick, disabled, full, size, style={} }) => (
  <button onClick={onClick} disabled={disabled}
    className={`btn btn-${color}${full?' btn-full':''}${size==='sm'?' btn-sm':''}`}
    style={style}>{children}</button>
);
const Field = ({ label, children }) => <div className="field">{label&&<label className="flabel">{label}</label>}{children}</div>;
const Inp = ({ label, ...p }) => <Field label={label}><input className="inp" {...p}/></Field>;
const Sel = ({ label, children, ...p }) => <Field label={label}><select className="sel" {...p}>{children}</select></Field>;
const TA = ({ label, ...p }) => <Field label={label}><textarea className="ta" {...p}/></Field>;
const Card = ({ children, style }) => <div className="card" style={style}>{children}</div>;
const CT = ({ icon, children }) => <div className="card-title">{icon&&<div className="card-title-icon">{icon}</div>}{children}</div>;
const StatCard = ({ val, label, icon, bg, ac }) => (
  <div className="stat-card" style={{'--ac':ac}}>
    <div className="stat-icon" style={{background:bg||'rgba(0,200,83,0.15)'}}>{icon}</div>
    <div className="stat-val">{val}</div>
    <div className="stat-label">{label}</div>
  </div>
);

// ============================================================
// LOGIN PAGE
// ============================================================
function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const doLogin = async () => {
    if (!email || !pass) return setErr('Email and password required');
    setLoading(true); setErr('');
    const r = await api('auth.php?action=login', 'POST', { email, password: pass });
    if (r.status === 'success') {
      localStorage.setItem('ev_token', r.data.token);
      onLogin(r.data.user);
    } else {
      setErr(r.message || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div style={{textAlign:'center',marginBottom:32}}>
          <img src={logo} alt="EssenceVeda" style={{height:72,objectFit:'contain',marginBottom:14}}/>
          <h2 style={{fontFamily:'Poppins,sans-serif',fontSize:22,fontWeight:700,color:'#fff',marginBottom:6}}>CRM Login</h2>
          <p style={{color:'rgba(255,255,255,0.4)',fontSize:13}}>EssenceVeda Life — Telecalling System</p>
        </div>
        {err && <div style={{background:'rgba(244,67,54,0.15)',border:'1px solid rgba(244,67,54,0.3)',borderRadius:8,padding:'10px 14px',color:'#f44336',fontSize:13,marginBottom:16}}>{err}</div>}
        <Field label="Email">
          <input className="inp" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" onKeyDown={e=>e.key==='Enter'&&doLogin()} style={{background:'rgba(255,255,255,0.08)',borderColor:'rgba(255,255,255,0.15)',color:'#fff'}}/>
        </Field>
        <Field label="Password">
          <input className="inp" type="password" value={pass} onChange={e=>setPass(e.target.value)} placeholder="••••••••" onKeyDown={e=>e.key==='Enter'&&doLogin()} style={{background:'rgba(255,255,255,0.08)',borderColor:'rgba(255,255,255,0.15)',color:'#fff'}}/>
        </Field>
        <button onClick={doLogin} disabled={loading} className="btn btn-green btn-full" style={{fontSize:15,padding:'13px 20px'}}>
          {loading ? 'Logging in...' : 'Login'}
        </button>
        <p style={{textAlign:'center',color:'rgba(255,255,255,0.25)',fontSize:11,marginTop:20}}>EssenceVeda CRM v3.0</p>
      </div>
    </div>
  );
}

// ============================================================
// SIDEBAR
// ============================================================
const ICONS = {
  dashboard: '📊', upload: '📤', assign: '🎯', accounts: '👥', products: '📦',
  blacklist: '🚫', analytics: '📈', settings: '⚙️', calls: '📞', followups: '📅',
  myleads: '📋', performance: '🏆', leaderboard: '🥇', messages: '💬', search: '🔍',
  profile: '👤', report: '📄', team: '👁️', punch: '📝', redistribute: '🔄'
};

const NAV = {
  admin: [
    {id:'dashboard',label:'Dashboard',icon:'dashboard'},
    {id:'data',label:'Data Management',icon:'upload'},
    {id:'assign',label:'Assignment',icon:'assign'},
    {id:'accounts',label:'Accounts',icon:'accounts'},
    {id:'products',label:'Products',icon:'products'},
    {id:'blacklist',label:'Blacklist',icon:'blacklist'},
    {id:'reports',label:'Reports',icon:'report'},
    {id:'settings',label:'Settings',icon:'settings'},
  ],
  tl: [
    {id:'dashboard',label:'Dashboard',icon:'dashboard'},
    {id:'data',label:'My Data',icon:'myleads'},
    {id:'assign',label:'Assign Leads',icon:'assign'},
    {id:'team',label:'Team Monitor',icon:'team'},
    {id:'followups',label:'Follow Ups',icon:'followups'},
    {id:'report',label:'Reports',icon:'report'},
    {id:'messages',label:'Messages',icon:'messages'},
    {id:'callers',label:'My Callers',icon:'accounts'},
  ],
  caller: [
    {id:'calling',label:'Calling',icon:'calls'},
    {id:'punch',label:'My Punches',icon:'punch'},
    {id:'followups',label:'Follow Ups',icon:'followups'},
    {id:'myleads',label:'My Leads',icon:'myleads'},
    {id:'performance',label:'Performance',icon:'performance'},
    {id:'leaderboard',label:'Leaderboard',icon:'leaderboard'},
    {id:'messages',label:'Messages',icon:'messages'},
    {id:'search',label:'Search Lead',icon:'search'},
    {id:'profile',label:'Profile',icon:'profile'},
  ]
};

function Sidebar({ user, page, onNav, onLogout, notifCount }) {
  const items = NAV[user.role] || [];
  return (
    <div className="sidebar">
      <div className="sb-logo">
        <img src={logo} alt="EssenceVeda"/>
        <div className="sb-brand">
          <div className="sb-brand-name">EssenceVeda Life</div>
          <div className="sb-brand-sub">Telecalling CRM</div>
        </div>
      </div>
      <div className="sb-role-badge">{user.role} Panel</div>
      <nav className="sb-nav">
        {items.map(item => (
          <button key={item.id} className={`nav-item${page===item.id?' active':''}`} onClick={()=>onNav(item.id)}>
            <span style={{fontSize:16,width:22,textAlign:'center',flexShrink:0}}>{ICONS[item.icon]}</span>
            <span style={{flex:1}}>{item.label}</span>
            {item.id==='messages' && notifCount>0 && <span className="nav-badge-count">{notifCount}</span>}
          </button>
        ))}
      </nav>
      <div className="sb-footer">
        <div className="sb-user">
          <div className="sb-avatar">{user.name[0].toUpperCase()}</div>
          <div>
            <div className="sb-uname">{user.name}</div>
            <div className="sb-urole">{user.role}</div>
          </div>
        </div>
        <button className="logout-btn" onClick={onLogout}>🚪 Logout</button>
      </div>
    </div>
  );
}

// ============================================================
// ADMIN - DASHBOARD
// ============================================================
function AdminDashboard() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const r = await api('reports.php?action=analytics');
    if (r.status === 'success') setData(r.data);
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  if (!data) return <Spin/>;
  const o = data.overall || {};
  return (
    <>
      <div className="stat-grid">
        <StatCard val={o.total_leads||0} label="Total Leads" icon="📋" bg="rgba(33,150,243,0.15)" ac="linear-gradient(90deg,#2196f3,#42a5f5)"/>
        <StatCard val={o.fresh||0} label="Fresh (Total)" icon="🌱" bg="rgba(0,200,83,0.15)" ac="linear-gradient(90deg,#00c853,#69f0ae)"/>
        <StatCard val={(o.fresh||0)-(o.fresh_unassigned||0)} label="Fresh (Assigned)" icon="✔️" bg="rgba(33,150,243,0.15)" ac="linear-gradient(90deg,#2196f3,#42a5f5)"/>
        <StatCard val={o.fresh_unassigned||0} label="Fresh (Remaining)" icon="⭕" bg="rgba(255,193,7,0.15)" ac="linear-gradient(90deg,#ffc107,#ffd54f)"/>
        <StatCard val={o.converted||0} label="Converted" icon="✅" bg="rgba(255,107,43,0.15)" ac="linear-gradient(90deg,#ff6b2b,#ff9a3c)"/>
      </div>
      <div className="g2">
        <Card>
          <CT icon="📦">Product Performance</CT>
          {(data.product_wise||[]).map(p=>(
            <div key={p.name} style={{marginBottom:16}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                <span style={{fontWeight:600}}>{p.name}</span>
                <span style={{color:'var(--green)',fontWeight:700}}>{p.converted}/{p.total} ({p.conversion_rate||0}%)</span>
              </div>
              <div className="progress-wrap"><div className="progress-bar" style={{width:`${p.conversion_rate||0}%`}}/></div>
            </div>
          ))}
          {!(data.product_wise?.length) && <Empty title="No product data yet"/>}
        </Card>
        <Card>
          <CT icon="🏆">Top Callers Today</CT>
          {(data.top_callers||[]).map((c,i)=>(
            <div key={c.name} style={{display:'flex',alignItems:'center',gap:12,padding:'11px 0',borderBottom:'1px solid var(--card-border)'}}>
              <span style={{fontSize:20,width:32}}>{['🥇','🥈','🥉','4️⃣','5️⃣'][i]}</span>
              <span style={{flex:1,fontWeight:600}}>{c.name}</span>
              <span style={{fontSize:22,fontWeight:800,color:'var(--green)',fontFamily:'Poppins,sans-serif'}}>{c.converted}</span>
            </div>
          ))}
          {!(data.top_callers?.length) && <Empty title="No data yet"/>}
        </Card>
      </div>
      <Card>
        <CT icon="👔">TL Overview</CT>
        <table className="tbl">
          <thead><tr><th>TL Name</th><th>Total Leads</th><th>Converted</th><th>Team Size</th></tr></thead>
          <tbody>
            {(data.tl_stats||[]).map(t=>(
              <tr key={t.id}>
                <td style={{fontWeight:600}}>{t.name}</td>
                <td>{t.total_leads}</td>
                <td style={{color:'var(--green)',fontWeight:700}}>{t.converted}</td>
                <td>{t.callers} callers</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!(data.tl_stats?.length) && <Empty title="No TLs yet"/>}
      </Card>
    </>
  );
}

// ============================================================
// DATA MANAGEMENT (Admin + TL)
// ============================================================
function DataManagement({ user, products }) {
  const [pool, setPool] = useState('fresh');
  const [productId, setProductId] = useState('');
  const [search, setSearch] = useState('');
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pasteData, setPasteData] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [assignUser, setAssignUser] = useState('');
  const [users, setUsers] = useState([]);
  const [showHistory, setShowHistory] = useState(null);
  const [history, setHistory] = useState([]);
  const [showRollConfirm, setShowRollConfirm] = useState(false);
  const [recentDays, setRecentDays] = useState(()=>parseInt(localStorage.getItem('ev_recent_days')||'7'));
  const [uploadType, setUploadType] = useState('fresh'); // fresh or recent

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({pool,page,limit:50});
    if(productId) params.append('product_id',productId);
    if(search) params.append('search',search);
    const r = await api(`leads.php?action=list&${params}`);
    if(r.status==='success'){setLeads(r.data.leads||[]);setTotal(r.data.total||0);}
    setLoading(false);
  }, [pool, productId, search, page]);

  useEffect(()=>{load();},[load]);
  useEffect(()=>{
    if(user.role==='admin'){
      api('users.php?action=list&role=tl').then(r=>{
        if(r.status==='success') setUsers(r.data);
      });
    } else {
      // TL - get their callers directly
      api('users.php?action=list').then(r=>{
        if(r.status==='success') setUsers(r.data.filter(u=>u.role==='caller'&&String(u.tl_id)===String(user.id)));
      });
    }
  },[user]);

  const doUpload = async (pool='fresh') => {
    if(!productId) return toast('Select a product','error');
    if(!pasteData.trim()) return toast('Paste data first','error');
    const rows = pasteData.trim().split('\n').map(line=>{
      const parts = line.split(/[,\t]/);
      return parts.length>=2?{name:parts[0].trim(),phone:parts[1].trim()}:{name:'',phone:parts[0].trim()};
    }).filter(r=>r.phone&&r.phone.replace(/\D/,'').length>=10);
    if(!rows.length) return toast('No valid phone numbers found','error');
    setUploading(true);
    const r = await api('leads.php?action=upload','POST',{product_id:productId,leads:rows,pool});
    if(r.status==='success'){
      setUploadResult(r.data);
      toast(`${r.data.imported} leads uploaded as ${pool}!`);
      if(r.data.conflicts?.length>0){
        toast(`⚠️ ${r.data.conflicts.length} numbers already assigned — check below`,'warning');
      }
      load();
    }
    else toast(r.message,'error');
    setUploading(false);
  };

  const handleFile = e=>{
    const f=e.target.files[0];if(!f)return;
    const reader=new FileReader();reader.onload=ev=>setPasteData(ev.target.result);reader.readAsText(f);
  };

  const toggleSelect = id=>{
    setSelectedIds(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);
  };
  const selectAll = ()=>setSelectedIds(leads.map(l=>l.id));
  const clearSelect = ()=>setSelectedIds([]);

  const assignSelected = async()=>{
    if(!assignUser||!selectedIds.length) return toast('Select leads and a user','error');
    const r=await api('leads.php?action=assign_manual','POST',{lead_ids:selectedIds,to_id:assignUser});
    if(r.status==='success'){toast(`${r.data.assigned} leads assigned!`);clearSelect();load();}
    else toast(r.message,'error');
  };

  const viewHistory = async(leadId)=>{
    setShowHistory(leadId);
    const r=await api(`leads.php?action=history&lead_id=${leadId}`);
    if(r.status==='success') setHistory(r.data);
  };

  const poolColor = {fresh:'green',recent:'orange',followup:'yellow',converted:'blue'};
  const pools = user.role==='admin'?['fresh','recent','assigned','converted']:['fresh','recent','followup','converted'];

  return (
    <>
      {user.role==='admin' && (
        <div style={{marginBottom:16,display:'flex',justifyContent:'flex-end'}}>
          <Btn color="green" onClick={()=>setShowUpload(!showUpload)}>📤 Upload Data</Btn>
        </div>
      )}
      {user.role==='tl' && (
        <div style={{marginBottom:16,display:'flex',justifyContent:'flex-end',gap:10,alignItems:'center'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,background:'var(--card)',border:'1px solid var(--card-border)',borderRadius:'var(--r-sm)',padding:'8px 12px'}}>
            <span style={{fontSize:12,color:'var(--text2)',fontWeight:600}}>Recent Range:</span>
            <select className="sel" value={recentDays} onChange={e=>{
              const v=parseInt(e.target.value);
              setRecentDays(v);
              localStorage.setItem('ev_recent_days',v);
              toast(`Recent data range set to ${v===0?'All':'last '+v+' days'}`,'info');
            }} style={{width:130,marginBottom:0,padding:'5px 10px',fontSize:12}}>
              <option value="3">Last 3 days</option>
              <option value="7">Last 7 days</option>
              <option value="15">Last 15 days</option>
              <option value="30">Last 30 days</option>
              <option value="0">All time</option>
            </select>
          </div>
          <Btn color="orange" onClick={()=>setShowRollConfirm(true)}>🔄 Roll Recent Data</Btn>
        </div>
      )}

      {/* Roll Recent Confirm Modal */}
      <div className={`modal-bg${showRollConfirm?' open':''}`} onClick={e=>e.target===e.currentTarget&&setShowRollConfirm(false)}>
        <div className="modal" style={{maxWidth:400}}>
          <div className="modal-title">🔄 Roll Recent Data</div>
          <p style={{color:'var(--text2)',fontSize:14,marginBottom:20,lineHeight:1.7}}>
            Callers ke paas jo <strong>recent leads</strong> hain — wo wapas tumhare pool mein aa jaayengi.<br/>
            <span style={{color:'var(--red)',fontSize:13}}>Callers ke paas se ye leads hat jaayengi!</span>
          </p>
          <div style={{background:'rgba(255,107,43,0.08)',border:'1px solid rgba(255,107,43,0.2)',borderRadius:'var(--r-sm)',padding:'12px 16px',marginBottom:20}}>
            <div style={{fontSize:13,color:'var(--text2)'}}>Current Range: <strong style={{color:'var(--orange)'}}>{recentDays===0?'All time':'Last '+recentDays+' days'}</strong></div>
            <div style={{fontSize:12,color:'var(--text3)',marginTop:4}}>Sirf is range ke leads roll honge</div>
          </div>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <Btn color="ghost" onClick={()=>setShowRollConfirm(false)}>Cancel</Btn>
            <Btn color="orange" onClick={async()=>{
              const r=await api('leads.php?action=roll_recent','POST',{product_id:productId||null,days:recentDays});
              if(r.status==='success'){toast(`${r.data.rolled} leads rolled back!`);setShowRollConfirm(false);load();}
              else toast(r.message,'error');
            }}>Confirm Roll</Btn>
          </div>
        </div>
      </div>

      {showUpload && user.role==='admin' && (
        <Card>
          <div style={{display:'flex',gap:8,marginBottom:16}}>
            <button className={`btn btn-sm ${uploadType==='fresh'?'btn-green':'btn-ghost'}`} onClick={()=>setUploadType('fresh')}>🌱 Upload Fresh</button>
            <button className={`btn btn-sm ${uploadType==='recent'?'btn-orange':'btn-ghost'}`} onClick={()=>setUploadType('recent')}>🔄 Upload Recent</button>
          </div>
          <CT icon="📤">{uploadType==='fresh'?'Upload Fresh Data':'Upload Recent Data'}</CT>
          <div className="g2">
            <div>
              <Sel label="Product" value={productId} onChange={e=>setProductId(e.target.value)}>
                <option value="">-- Select Product --</option>
                {products.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
              </Sel>
              <Field label="CSV File">
                <input type="file" accept=".csv,.txt" onChange={handleFile} style={{color:'var(--text2)',fontSize:13}}/>
              </Field>
              <Btn color={uploadType==='fresh'?'green':'orange'} full onClick={()=>doUpload(uploadType)} disabled={uploading}>{uploading?'Uploading...':'Upload'}</Btn>
            </div>
            <div>
              <TA label="Or Paste Data Here (Name, Phone — one per line)" value={pasteData} onChange={e=>setPasteData(e.target.value)} rows={8} placeholder={"Ramesh Kumar, 9876543210\nPriya Sharma, 9876543211"}/>
              {pasteData && <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>Last number: <strong style={{color:'var(--green)',fontFamily:'var(--mono)'}}>{pasteData.trim().split('\n').slice(-1)[0]?.split(/[,\t]/)[1]?.trim()||'—'}</strong></div>}
            </div>
          </div>
          {uploadResult && (
            <>
            <div className="stat-grid-3" style={{marginTop:16}}>
              <StatCard val={uploadResult.imported} label="Imported" icon="✅" bg="rgba(0,200,83,0.15)"/>
              <StatCard val={uploadResult.duplicates} label="Duplicates (same day)" icon="⚠️" bg="rgba(255,193,7,0.15)"/>
              <StatCard val={uploadResult.moved_to_fresh} label="Moved to Fresh" icon="🔄" bg="rgba(33,150,243,0.15)"/>
              <StatCard val={uploadResult.repeat_customers} label="Repeat Customers" icon="🔁" bg="rgba(156,39,176,0.15)"/>
              <StatCard val={uploadResult.blacklisted} label="Blacklisted" icon="🚫" bg="rgba(244,67,54,0.15)"/>
              {uploadResult.last_phone && <div style={{background:'rgba(0,200,83,0.1)',border:'1px solid rgba(0,200,83,0.2)',borderRadius:'var(--r-sm)',padding:16,display:'flex',flexDirection:'column',gap:4}}>
                <div style={{fontSize:11,color:'var(--text3)',fontWeight:700}}>LAST NUMBER</div>
                <div style={{fontFamily:'var(--mono)',fontSize:16,color:'var(--green)',fontWeight:700}}>{uploadResult.last_phone}</div>
              </div>}
            </div>
            {uploadResult.conflicts?.length>0 && (
              <div style={{marginTop:16,background:'rgba(255,193,7,0.08)',border:'1px solid rgba(255,193,7,0.3)',borderRadius:'var(--r)',padding:16}}>
                <div style={{fontWeight:700,color:'var(--yellow)',marginBottom:12}}>⚠️ {uploadResult.conflicts.length} Numbers Already Assigned</div>
                <table className="tbl">
                  <thead><tr><th>Phone</th><th>Name</th><th>Status</th><th>Assigned To</th></tr></thead>
                  <tbody>{uploadResult.conflicts.map((c,i)=>(
                    <tr key={i}>
                      <td style={{fontFamily:'var(--mono)',color:'var(--orange)',fontWeight:700}}>{c.phone}</td>
                      <td>{c.name||'—'}</td>
                      <td><Badge color={c.pool==='followup'?'yellow':'orange'}>{c.pool}</Badge></td>
                      <td style={{color:'var(--text2)'}}>{c.assigned_to}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            </>
          )}
        </Card>
      )}

      <Card>
        <div className="tabs">
          {pools.map(p=><button key={p} className={`tab${pool===p?' active':''}`} onClick={()=>{setPool(p);setPage(1);setSelectedIds([])}} style={{textTransform:'capitalize'}}>{p}</button>)}
        </div>

        <div style={{display:'flex',gap:12,marginBottom:16,flexWrap:'wrap'}}>
          <div className="search-bar" style={{flex:1,margin:0}}>
            <span style={{color:'var(--text3)'}}>🔍</span>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name or number..." onKeyDown={e=>e.key==='Enter'&&load()}/>
            {search && <button onClick={()=>setSearch('')} style={{background:'none',color:'var(--text3)',fontSize:16}}>✕</button>}
          </div>
          <Sel label="" value={productId} onChange={e=>{setProductId(e.target.value);setPage(1)}} style={{width:160,marginBottom:0}}>
            <option value="">All Products</option>
            {products.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </Sel>
        </div>

        {selectedIds.length>0 && (
          <div style={{display:'flex',gap:10,alignItems:'center',padding:'12px 16px',background:'rgba(0,200,83,0.08)',border:'1px solid rgba(0,200,83,0.2)',borderRadius:'var(--r-sm)',marginBottom:16,flexWrap:'wrap'}}>
            <span style={{color:'var(--green)',fontWeight:600}}>{selectedIds.length} selected</span>
            <select className="sel" value={assignUser} onChange={e=>setAssignUser(e.target.value)} style={{width:180,marginBottom:0,padding:'7px 12px'}}>
              <option value="">Select {user.role==='admin'?'TL':'Caller'}</option>
              {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <Btn color="green" size="sm" onClick={assignSelected}>Assign Selected</Btn>
            <Btn color="ghost" size="sm" onClick={selectAll}>Select All</Btn>
            <Btn color="ghost" size="sm" onClick={clearSelect}>Clear</Btn>
          </div>
        )}

        <div style={{color:'var(--text3)',fontSize:12,marginBottom:12}}>{total} total records · Page {page}</div>

        {loading ? <Spin/> : leads.length===0 ? <Empty title={`No ${pool} leads`}/> : (
          <div style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr>
                <th><input type="checkbox" onChange={e=>e.target.checked?selectAll():clearSelect()} style={{cursor:'pointer'}}/></th>
                <th>#</th><th>Name</th><th>Phone</th><th>Product</th>
                <th>{user.role==='admin'?'Assigned To TL':'Assigned To Caller'}</th>
                <th>Last Status</th><th>Last Remark</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {leads.map((l,idx)=>(
                  <tr key={l.id}>
                    <td><input type="checkbox" checked={selectedIds.includes(l.id)} onChange={()=>toggleSelect(l.id)} style={{cursor:'pointer'}}/></td>
                    <td style={{color:'var(--text3)',fontSize:12}}>{(page-1)*50+idx+1}</td>
                    <td style={{fontWeight:600}}>{l.name||<span style={{color:'var(--text3)'}}>—</span>}</td>
                    <td style={{fontFamily:'var(--mono)',color:'var(--green)',fontWeight:700}}>{l.phone}</td>
                    <td><Badge color="blue">{l.product_name}</Badge></td>
                    <td style={{color:'var(--text2)'}}>{
                      user.role==='admin'
                        ? (l.tl_name || <Badge color="gray">Unassigned</Badge>)
                        : (l.caller_name || <Badge color="gray">Unassigned</Badge>)
                    }</td>
                    <td>{l.last_disposition?<Badge color={l.last_disposition==='CONV'?'green':l.last_disposition==='FU'?'orange':'gray'}>{l.last_disposition}</Badge>:<span style={{color:'var(--text3)'}}>—</span>}</td>
                    <td style={{color:'var(--text3)',fontSize:12,maxWidth:150}}>{l.last_remark||'—'}</td>
                    <td style={{display:'flex',gap:6}}>
                      <Btn size="sm" color="ghost" onClick={()=>viewHistory(l.id)}>History</Btn>
                      {selectedIds.length===0 && pool!=='converted' && (
                        <Btn size="sm" color="green" onClick={()=>setSelectedIds([l.id])}>Assign</Btn>
                      )}
                      {user.role==='admin' && pool!=='converted' && (
                        <Btn size="sm" color="blue" onClick={async()=>{
                          if(!window.confirm('Mark as converted?')) return;
                          const r=await api('leads.php?action=admin_convert','POST',{lead_id:l.id});
                          if(r.status==='success'){toast('Converted!');load();}
                          else toast(r.message,'error');
                        }}>Convert</Btn>
                      )}
                      {user.role==='admin' && pool==='converted' && (
                        <Btn size="sm" color="orange" onClick={async()=>{
                          if(!window.confirm('Move back to recent pool?')) return;
                          const r=await api('leads.php?action=admin_to_recent','POST',{lead_id:l.id});
                          if(r.status==='success'){toast('Moved to recent!');load();}
                          else toast(r.message,'error');
                        }}>→ Recent</Btn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{display:'flex',gap:10,marginTop:16,justifyContent:'center'}}>
          <Btn color="ghost" size="sm" disabled={page===1} onClick={()=>setPage(p=>p-1)}>← Prev</Btn>
          <span style={{padding:'7px 14px',color:'var(--text2)'}}>Page {page}</span>
          <Btn color="ghost" size="sm" disabled={leads.length<50} onClick={()=>setPage(p=>p+1)}>Next →</Btn>
        </div>
      </Card>

      {/* History Modal */}
      <div className={`modal-bg${showHistory?' open':''}`} onClick={e=>e.target===e.currentTarget&&setShowHistory(null)}>
        <div className="modal">
          <div className="modal-title">📋 Lead History</div>
          {history.length===0?<Empty title="No history yet"/>:
            history.map((h,i)=>(
              <div key={i} className="hist-item">
                <span className="hist-time">{h.attempted_at?.slice(0,16)}</span>
                <Badge color={h.disposition==='CONV'?'green':h.disposition==='FU'?'orange':'gray'}>{h.disposition}</Badge>
                <div style={{flex:1}}>
                  {h.remark&&<div style={{color:'var(--text)',fontSize:13,marginTop:4}}>{h.remark}</div>}
                  <div style={{color:'var(--text3)',fontSize:11,marginTop:2}}>by {h.by_name}</div>
                </div>
              </div>
            ))
          }
          <div style={{marginTop:20,display:'flex',justifyContent:'flex-end'}}>
            <Btn color="ghost" onClick={()=>setShowHistory(null)}>Close</Btn>
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================
// ASSIGNMENT (Admin + TL)
// ============================================================
function Assignment({ user, products }) {
  const [mode, setMode] = useState('bulk');
  const [form, setForm] = useState({ product_id:'', pool:'fresh', to_id:'', quantity:10, is_priority:false });
  const [users, setUsers] = useState([]);
  const [poolStats, setPoolStats] = useState([]);
  const [result, setResult] = useState(null);
  const [minusForm, setMinusForm] = useState({ from_id:'', product_id:'', quantity:5 });
  const [loading, setLoading] = useState(false);
  const [recentDays, setRecentDays] = useState(()=>parseInt(localStorage.getItem('ev_recent_days')||'7'));

  useEffect(()=>{
    if(user.role==='admin'){
      api('users.php?action=list&role=tl').then(r=>{if(r.status==='success')setUsers(r.data);});
    } else {
      api('users.php?action=list').then(r=>{
        if(r.status==='success') setUsers(r.data.filter(u=>u.role==='caller'&&String(u.tl_id)===String(user.id)));
      });
    }
  },[user]);

  useEffect(()=>{
    if(form.product_id){
      api(`leads.php?action=pool_stats&product_id=${form.product_id}`).then(r=>{if(r.status==='success')setPoolStats(r.data);});
    }
  },[form.product_id]);

  const assign = async()=>{
    if(!form.product_id||!form.to_id||!form.quantity) return toast('All fields required','error');
    setLoading(true);
    const r=await api('leads.php?action=assign_bulk','POST',{...form,is_priority:form.is_priority?1:0,days:recentDays});
    if(r.status==='success'){setResult(r.data);toast(`${r.data.assigned} leads assigned!`);}
    else toast(r.message,'error');
    setLoading(false);
  };

  const doMinus = async()=>{
    if(!minusForm.from_id||!minusForm.quantity) return toast('Fill all fields','error');
    const r=await api('leads.php?action=minus_leads','POST',minusForm);
    if(r.status==='success') toast(`${r.data.returned} leads returned silently`);
    else toast(r.message,'error');
  };

  const poolStat = (p) => poolStats.find(s=>s.pool===p)||{total:0,unassigned:0,assigned:0};

  return (
    <>
      <div className="tabs" style={{maxWidth:300}}>
        <button className={`tab${mode==='bulk'?' active':''}`} onClick={()=>setMode('bulk')}>Bulk Assign</button>
        <button className={`tab${mode==='minus'?' active':''}`} onClick={()=>setMode('minus')}>Minus Leads</button>
      </div>

      {mode==='bulk' && (
        <div className="g2">
          <Card>
            <CT icon="🎯">Assign Leads</CT>
            <Sel label="Product" value={form.product_id} onChange={e=>setForm({...form,product_id:e.target.value})}>
              <option value="">-- Select Product --</option>
              {products.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </Sel>
            <Sel label="Pool" value={form.pool} onChange={e=>setForm({...form,pool:e.target.value})}>
              <option value="fresh">Fresh Data</option>
              <option value="recent">Recent Data</option>
            </Sel>
            {form.pool==='recent' && (
              <div>
                <label className="flabel">Recent Data Range</label>
                <select className="sel" value={recentDays} onChange={e=>{
                  const v=parseInt(e.target.value);
                  setRecentDays(v);
                  localStorage.setItem('ev_recent_days',v);
                }}>
                  <option value="3">Last 3 days</option>
                  <option value="7">Last 7 days</option>
                  <option value="15">Last 15 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="0">All time</option>
                </select>
                <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>Sirf is range ke recent leads assign honge</div>
              </div>
            )}
            <Sel label={user.role==='admin'?'Team Lead':'Caller'} value={form.to_id} onChange={e=>setForm({...form,to_id:e.target.value})}>
              <option value="">-- Select --</option>
              {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </Sel>
            <Inp label="Number of Leads" type="number" value={form.quantity} onChange={e=>setForm({...form,quantity:e.target.value})} min={1} max={500}/>
            <label style={{display:'flex',alignItems:'center',gap:8,color:'var(--text2)',fontSize:13,cursor:'pointer',marginBottom:16}}>
              <input type="checkbox" checked={form.is_priority} onChange={e=>setForm({...form,is_priority:e.target.checked})}/>
              Mark as Priority
            </label>
            {result && <div style={{background:'rgba(0,200,83,0.1)',border:'1px solid rgba(0,200,83,0.2)',borderRadius:'var(--r-sm)',padding:14,marginBottom:16,color:'var(--green)',fontWeight:600}}>✅ {result.assigned} leads assigned!</div>}
            <Btn color="green" full onClick={assign} disabled={loading}>{loading?'Assigning...':'Assign Leads'}</Btn>
          </Card>
          <Card>
            <CT icon="📊">Pool Status</CT>
            {form.product_id ? (
              <>
                {['fresh','recent','followup','converted'].map(p=>{
                  const s=poolStat(p);
                  return(
                    <div key={p} style={{padding:'14px 0',borderBottom:'1px solid var(--card-border)'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                        <Badge color={p==='fresh'?'green':p==='recent'?'orange':p==='followup'?'yellow':'blue'} style={{textTransform:'capitalize'}}>{p}</Badge>
                        <span style={{color:'var(--text)',fontWeight:700,fontSize:18}}>{s.total}</span>
                      </div>
                      <div style={{display:'flex',gap:20,fontSize:12,color:'var(--text3)'}}>
                        <span>Unassigned: <strong style={{color:'var(--green)'}}>{s.unassigned}</strong></span>
                        <span>Assigned: <strong style={{color:'var(--orange)'}}>{s.assigned}</strong></span>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : <Empty title="Select a product to view pool stats"/>}
          </Card>
        </div>
      )}

      {mode==='minus' && (
        <Card>
          <CT icon="➖">Minus Leads (Silent)</CT>
          <p style={{color:'var(--text3)',fontSize:13,marginBottom:20}}>Returns only untouched (attempt=0) leads back to pool. No notification sent.</p>
          <div className="g2">
            <div>
              <Sel label={user.role==='admin'?'From TL':'From Caller'} value={minusForm.from_id} onChange={e=>setMinusForm({...minusForm,from_id:e.target.value})}>
                <option value="">-- Select --</option>
                {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
              </Sel>
              <Sel label="Product (optional)" value={minusForm.product_id} onChange={e=>setMinusForm({...minusForm,product_id:e.target.value})}>
                <option value="">All Products</option>
                {products.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
              </Sel>
              <Inp label="Quantity to Return" type="number" value={minusForm.quantity} onChange={e=>setMinusForm({...minusForm,quantity:e.target.value})} min={1}/>
              <Btn color="orange" full onClick={doMinus}>Return Leads Silently</Btn>
            </div>
            <div style={{background:'rgba(255,193,7,0.05)',border:'1px solid rgba(255,193,7,0.15)',borderRadius:'var(--r)',padding:20}}>
              <h4 style={{color:'var(--yellow)',marginBottom:12}}>⚠️ Important Rules</h4>
              <ul style={{color:'var(--text2)',fontSize:13,lineHeight:2,paddingLeft:16}}>
                <li>Only leads with 0 attempts will be returned</li>
                <li>Leads already attempted stay with the user</li>
                <li>No notification sent to anyone</li>
                <li>Returned leads go back to unassigned pool</li>
                <li>Follow up and converted leads are never returned</li>
              </ul>
            </div>
          </div>
        </Card>
      )}
    </>
  );
}

// ============================================================
// ADMIN - ACCOUNTS (Hierarchy Accordion)
// ============================================================
function AdminAccounts() {
  const [tls, setTls] = useState([]);
  const [inactiveTls, setInactiveTls] = useState([]);
  const [inactiveCallers, setInactiveCallers] = useState([]);
  const [open, setOpen] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name:'', email:'', password:'Pass@123', role:'caller', tl_id:'', is_exempt:false });
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async()=>{
    const r=await api('users.php?action=hierarchy');
    if(r.status==='success'){
      const activeTls = r.data.filter(t=>t.is_active);
      const inTls = r.data.filter(t=>!t.is_active);
      const inCallers = r.data.flatMap(t=>t.callers.filter(c=>!c.is_active));
      setTls(activeTls);
      setInactiveTls(inTls);
      setInactiveCallers(inCallers);
    }
    setLoading(false);
  },[]);

  useEffect(()=>{load();},[load]);

  const fmtTime = (dt)=>{
    if(!dt) return 'Never';
    const d = new Date(dt);
    return d.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})+' '+
           d.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true});
  };

  const create = async()=>{
    const r=await api('users.php?action=create','POST',{...form,is_exempt:form.is_exempt?1:0});
    if(r.status==='success'){toast('Account created! Password: '+form.password);setShowModal(false);load();setForm({name:'',email:'',password:'Pass@123',role:'caller',tl_id:'',is_exempt:false});}
    else toast(r.message,'error');
  };

  const update = async()=>{
    const payload = {id:editUser.id,...editForm,is_exempt:editForm.is_exempt?1:0};
    if(!editForm.password) delete payload.password;
    const r=await api('users.php?action=update','PUT',payload);
    if(r.status==='success'){toast('Updated!');setEditUser(null);load();}
    else toast(r.message,'error');
  };

  const toggleUser = async(id,isActive)=>{
    await api('users.php?action=update','PUT',{id,is_active:isActive?0:1});
    toast(isActive?'User deactivated':'User activated');load();
  };

  const resetPass = async(id)=>{
    const p='Pass@'+Math.floor(1000+Math.random()*9000);
    await api('users.php?action=update','PUT',{id,password:p});
    toast(`New password: ${p} — Note this down!`,'info');
  };

  const deleteUser = async(u)=>{
    const msg = u.role==='caller'
      ? `Delete ${u.name}? Their leads will go back to TL pool.`
      : `Delete TL ${u.name}? All their leads and callers will be unassigned!`;
    if(!window.confirm(msg)) return;
    const r = await api('users.php?action=delete','DELETE',{id:u.id});
    if(r.status==='success'){toast(u.name+' deleted. Data transferred.','info');load();}
    else toast(r.message,'error');
  };

  const TLCard = ({tl})=>(
    <div key={tl.id} className="accordion-item">
      <div className="accordion-header" onClick={()=>setOpen(p=>({...p,[tl.id]:!p[tl.id]}))}>
        <div style={{width:36,height:36,borderRadius:'50%',background:'linear-gradient(135deg,var(--green),var(--green2))',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,flexShrink:0}}>{tl.name[0]}</div>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,color:'var(--text)'}}>{tl.name}</div>
          <div style={{fontSize:12,color:'var(--text3)'}}>{tl.callers?.filter(c=>c.is_active).length||0} active callers · {tl.is_exempt?'Exempt':'Needs approval'} · Last: {fmtTime(tl.last_login)}</div>
        </div>
        <Badge color="green">Active TL</Badge>
        <div style={{display:'flex',gap:6,marginLeft:10}} onClick={e=>e.stopPropagation()}>
          <Btn size="sm" color="ghost" onClick={()=>{setEditUser(tl);setEditForm({name:tl.name,email:tl.email,is_exempt:tl.is_exempt,is_active:tl.is_active});}}>Edit</Btn>
          <Btn size="sm" color="ghost" onClick={()=>resetPass(tl.id)}>Reset</Btn>
          <Btn size="sm" color="red" onClick={()=>toggleUser(tl.id,tl.is_active)}>Deactivate</Btn>
          <Btn size="sm" color="red" onClick={()=>deleteUser(tl)}>Delete</Btn>
        </div>
        <span style={{marginLeft:10,color:'var(--text3)',fontSize:18}}>{open[tl.id]?'▲':'▼'}</span>
      </div>
      {open[tl.id] && (
        <div className="accordion-body">
          {tl.callers?.filter(c=>c.is_active).length===0
            ?<div style={{padding:'14px 0',color:'var(--text3)',fontSize:13}}>No active callers under this TL</div>
            :tl.callers?.filter(c=>c.is_active).map(c=>(
              <div key={c.id} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 0',borderBottom:'1px solid var(--card-border)'}}>
                <div style={{width:30,height:30,borderRadius:'50%',background:'rgba(255,107,43,0.15)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--orange)',fontWeight:700,fontSize:12,flexShrink:0}}>{c.name[0]}</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600,color:'var(--text)',fontSize:13}}>{c.name}</div>
                  <div style={{fontSize:11,color:'var(--text3)'}}>{c.email} · {c.is_exempt?'Exempt':'Needs approval'} · Last: {fmtTime(c.last_login)}</div>
                </div>
                <div style={{display:'flex',gap:6}}>
                  <Btn size="sm" color="ghost" onClick={()=>{setEditUser(c);setEditForm({name:c.name,email:c.email,is_exempt:c.is_exempt,is_active:c.is_active});}}>Edit</Btn>
                  <Btn size="sm" color="ghost" onClick={()=>resetPass(c.id)}>Reset</Btn>
                  <Btn size="sm" color="red" onClick={()=>toggleUser(c.id,c.is_active)}>Deactivate</Btn>
                  <Btn size="sm" color="red" onClick={()=>deleteUser(c)}>Delete</Btn>
                </div>
              </div>
            ))
          }
          <div style={{paddingTop:12}}>
            <Btn size="sm" color="green" onClick={()=>{setShowModal(true);setForm(f=>({...f,role:'caller',tl_id:tl.id}));}}>+ Add Caller to {tl.name}</Btn>
          </div>
        </div>
      )}
    </div>
  );

  if(loading) return <Spin/>;

  const totalActive = tls.length + tls.reduce((a,t)=>a+(t.callers?.filter(c=>c.is_active).length||0),0);
  const totalInactive = inactiveTls.length + inactiveCallers.length;

  return (
    <>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div style={{color:'var(--text2)',fontSize:14}}>
          <Badge color="green" style={{marginRight:8}}>{totalActive} Active</Badge>
          <Badge color="red">{totalInactive} Inactive</Badge>
        </div>
        <div style={{display:'flex',gap:10}}>
          <Btn color="ghost" onClick={()=>setShowInactive(!showInactive)}>{showInactive?'Hide':'Show'} Inactive ({totalInactive})</Btn>
          <Btn color="green" onClick={()=>setShowModal(true)}>+ New Account</Btn>
        </div>
      </div>

      {/* ACTIVE ACCOUNTS */}
      <div style={{marginBottom:8,color:'var(--green)',fontSize:12,fontWeight:700,textTransform:'uppercase',letterSpacing:1}}>Active Team Leads & Callers</div>
      {tls.length===0 && <div style={{color:'var(--text3)',marginBottom:16,fontSize:13}}>No active TLs yet</div>}
      {tls.map(tl=><TLCard key={tl.id} tl={tl}/>)}

      {/* INACTIVE ACCOUNTS */}
      {showInactive && (
        <>
          <hr className="divider"/>
          <div style={{marginBottom:8,color:'var(--red)',fontSize:12,fontWeight:700,textTransform:'uppercase',letterSpacing:1}}>Inactive Accounts</div>
          {inactiveTls.length===0 && inactiveCallers.length===0 && <div style={{color:'var(--text3)',fontSize:13,marginBottom:16}}>No inactive accounts</div>}
          {inactiveTls.map(tl=>(
            <div key={tl.id} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:'rgba(244,67,54,0.05)',border:'1px solid rgba(244,67,54,0.15)',borderRadius:'var(--r)',marginBottom:8}}>
              <div style={{width:36,height:36,borderRadius:'50%',background:'rgba(244,67,54,0.15)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--red)',fontWeight:700,flexShrink:0}}>{tl.name[0]}</div>
              <div style={{flex:1}}>
                <div style={{fontWeight:700}}>{tl.name} <Badge color="gray" style={{marginLeft:6}}>TL</Badge></div>
                <div style={{fontSize:11,color:'var(--text3)'}}>{tl.email} · Last: {fmtTime(tl.last_login)}</div>
              </div>
              <Btn size="sm" color="green" onClick={()=>toggleUser(tl.id,false)}>Activate</Btn>
            </div>
          ))}
          {inactiveCallers.map(c=>(
            <div key={c.id} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:'rgba(244,67,54,0.05)',border:'1px solid rgba(244,67,54,0.15)',borderRadius:'var(--r)',marginBottom:8}}>
              <div style={{width:36,height:36,borderRadius:'50%',background:'rgba(244,67,54,0.15)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--red)',fontWeight:700,flexShrink:0}}>{c.name[0]}</div>
              <div style={{flex:1}}>
                <div style={{fontWeight:700}}>{c.name} <Badge color="gray" style={{marginLeft:6}}>Caller</Badge></div>
                <div style={{fontSize:11,color:'var(--text3)'}}>{c.email} · Last: {fmtTime(c.last_login)}</div>
              </div>
              <Btn size="sm" color="green" onClick={()=>toggleUser(c.id,false)}>Activate</Btn>
            </div>
          ))}
        </>
      )}

      {/* Create Modal */}
      <div className={`modal-bg${showModal?' open':''}`} onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
        <div className="modal">
          <div className="modal-title">New Account</div>
          <Inp label="Full Name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="John Doe"/>
          <Inp label="Email" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="john@example.com"/>
          <Inp label="Password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})}/>
          <Sel label="Role" value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>
            <option value="caller">Caller</option>
            <option value="tl">Team Lead (TL)</option>
            <option value="admin">Admin</option>
          </Sel>
          {form.role==='caller' && (
            <Sel label="Assign to TL" value={form.tl_id} onChange={e=>setForm({...form,tl_id:e.target.value})}>
              <option value="">-- Select TL --</option>
              {tls.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
            </Sel>
          )}
          <label style={{display:'flex',alignItems:'center',gap:8,color:'var(--text2)',fontSize:13,cursor:'pointer',marginBottom:20}}>
            <input type="checkbox" checked={form.is_exempt} onChange={e=>setForm({...form,is_exempt:e.target.checked})}/>
            Exempt from login approval
          </label>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <Btn color="ghost" onClick={()=>setShowModal(false)}>Cancel</Btn>
            <Btn color="green" onClick={create}>Create Account</Btn>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <div className={`modal-bg${editUser?' open':''}`} onClick={e=>e.target===e.currentTarget&&setEditUser(null)}>
        <div className="modal">
          <div className="modal-title">Edit: {editUser?.name}</div>
          <Inp label="Name" value={editForm.name||''} onChange={e=>setEditForm({...editForm,name:e.target.value})}/>
          <Inp label="Email" value={editForm.email||''} onChange={e=>setEditForm({...editForm,email:e.target.value})}/>
          <Inp label="New Password (leave blank to keep)" value={editForm.password||''} onChange={e=>setEditForm({...editForm,password:e.target.value})} placeholder="New password..."/>
          <label style={{display:'flex',alignItems:'center',gap:8,color:'var(--text2)',fontSize:13,cursor:'pointer',marginBottom:14}}>
            <input type="checkbox" checked={!!editForm.is_exempt} onChange={e=>setEditForm({...editForm,is_exempt:e.target.checked})}/>
            Exempt from login approval
          </label>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <Btn color="ghost" onClick={()=>setEditUser(null)}>Cancel</Btn>
            <Btn color="green" onClick={update}>Save Changes</Btn>
          </div>
        </div>
      </div>
    </>
  );
}



// ============================================================
// ADMIN - PRODUCTS
// ============================================================
function AdminProducts() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name:'', script:'', min_price:0 });

  const load = async()=>{
    const r=await api('misc.php?type=products&action=list');
    if(r.status==='success') setList(r.data);
  };
  useEffect(()=>{load();},[]);

  const create = async()=>{
    if(!form.name.trim()) return toast('Product name required','error');
    const r=await api('misc.php?type=products&action=create','POST',form);
    if(r.status==='success'){toast('Product created!');setForm({name:'',script:'',min_price:0});load();}
    else toast(r.message,'error');
  };

  const doDelete = async(p)=>{
    const msg = `⚠️ Delete "${p.name}"?\n\n• ${p.total_leads} leads will be permanently deleted\n• All history, punch orders deleted\n\nYeh undo nahi hoga!`;
    if(!window.confirm(msg)) return;
    const r=await api('misc.php?type=products&action=delete','DELETE',{id:p.id,force:true});
    if(r.status==='success'){toast('Product deleted permanently','info');load();}
    else toast(r.message,'error');
  };

  const toggle = async(id,isActive)=>{
    await api('misc.php?type=products&action=update','PUT',{id,is_active:isActive?0:1});
    toast(isActive?'Product disabled — leads will not be assigned':'Product enabled');load();
  };

  return (
    <div className="g2">
      <Card>
        <CT icon="📦">Add Product</CT>
        <Inp label="Product Name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="NeuroVeda"/>
        <Inp label="Minimum Price (₹)" type="number" value={form.min_price} onChange={e=>setForm({...form,min_price:e.target.value})} min={0}/>
        <TA label="Calling Script (optional)" value={form.script} onChange={e=>setForm({...form,script:e.target.value})} rows={6} placeholder="Namaste! Main EssenceVeda se bol raha hoon..."/>
        <Btn color="green" full onClick={create}>Create Product</Btn>
      </Card>
      <Card>
        <CT icon="📋">All Products ({list.length})</CT>
        {list.map(p=>(
          <div key={p.id} style={{padding:'14px 0',borderBottom:'1px solid var(--card-border)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
              <div>
                <div style={{fontWeight:700,color:'var(--text)',fontSize:15}}>{p.name}</div>
                <div style={{color:'var(--text3)',fontSize:12,marginTop:2}}>Min Price: ₹{p.min_price}</div>
                <div style={{display:'flex',gap:12,marginTop:4,fontSize:12}}>
                  <span>Total: <strong style={{color:'var(--blue)'}}>{p.total_leads||0}</strong></span>
                  <span>Fresh: <strong style={{color:'var(--green)'}}>{p.fresh_count||0}</strong></span>
                  <span>Converted: <strong style={{color:'var(--orange)'}}>{p.converted_count||0}</strong></span>
                </div>
                {!p.is_active && <div style={{color:'var(--red)',fontSize:11,marginTop:4}}>⛔ Disabled — leads not being assigned</div>}
              </div>
              <Badge color={p.is_active?'green':'red'}>{p.is_active?'Active':'Disabled'}</Badge>
            </div>
            <div style={{display:'flex',gap:8}}>
              <Btn size="sm" color="ghost" onClick={()=>toggle(p.id,p.is_active)}>{p.is_active?'Disable':'Enable'}</Btn>
              <Btn size="sm" color="red" onClick={()=>doDelete(p)}>🗑️ Delete All</Btn>
            </div>
          </div>
        ))}
        {!list.length && <Empty title="No products yet"/>}
      </Card>
    </div>
  );
}


// ============================================================
// ADMIN - BLACKLIST
// ============================================================
function AdminBlacklist() {
  const [list, setList] = useState([]);
  const [phones, setPhones] = useState('');
  const [reason, setReason] = useState('');
  const [search, setSearch] = useState('');

  const load = async()=>{
    const r=await api(`misc.php?type=blacklist&action=list&search=${search}`);
    if(r.status==='success') setList(r.data);
  };
  useEffect(()=>{load();},[search]);

  const add = async()=>{
    const arr=phones.split('\n').map(p=>p.trim()).filter(Boolean);
    if(!arr.length) return toast('Enter phone numbers','error');
    const r=await api('misc.php?type=blacklist&action=add','POST',{phones:arr,reason,force:true});
    if(r.status==='success'){toast(`${r.data.added} numbers blacklisted`);setPhones('');setReason('');load();}
    else toast(r.message,'error');
  };

  const remove = async(id)=>{
    await api('misc.php?type=blacklist&action=remove','DELETE',{id});
    toast('Removed');load();
  };

  return (
    <div className="g2">
      <Card>
        <CT icon="🚫">Add to Blacklist</CT>
        <TA label="Phone Numbers (one per line)" value={phones} onChange={e=>setPhones(e.target.value)} rows={6} placeholder={"9876543210\n9876543211"}/>
        <Inp label="Reason (optional)" value={reason} onChange={e=>setReason(e.target.value)} placeholder="DND, Spam, etc."/>
        <Btn color="red" full onClick={add}>Add to Blacklist</Btn>
      </Card>
      <Card>
        <CT icon="📋">Blacklisted ({list.length})</CT>
        <div className="search-bar" style={{marginBottom:14}}>
          <span>🔍</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search number..."/>
        </div>
        <table className="tbl">
          <thead><tr><th>Phone</th><th>Reason</th><th>Date</th><th></th></tr></thead>
          <tbody>
            {list.map(b=>(
              <tr key={b.id}>
                <td style={{fontFamily:'var(--mono)',color:'var(--red)',fontWeight:700}}>{b.phone}</td>
                <td style={{color:'var(--text3)'}}>{b.reason||'—'}</td>
                <td style={{color:'var(--text3)',fontSize:12}}>{b.created_at?.slice(0,10)}</td>
                <td><Btn size="sm" color="ghost" onClick={()=>remove(b.id)}>Remove</Btn></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && <Empty title="Blacklist is empty"/>}
      </Card>
    </div>
  );
}

// ============================================================
// ADMIN - SETTINGS
// ============================================================
function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [disps, setDisps] = useState([]);
  const [newDisp, setNewDisp] = useState({ code:'', label:'', category:'recent' });

  useEffect(()=>{
    api('misc.php?type=settings&action=all').then(r=>{if(r.status==='success')setSettings(r.data);});
    api('misc.php?type=dispositions&action=list').then(r=>{if(r.status==='success')setDisps(r.data);});
  },[]);

  const save = async()=>{
    const r=await api('misc.php?type=settings&action=update','POST',settings);
    if(r.status==='success') toast('Settings saved!');
    else toast(r.message,'error');
  };

  const addDisp = async()=>{
    if(!newDisp.code||!newDisp.label) return toast('Code and label required','error');
    await api('misc.php?type=dispositions&action=create','POST',{...newDisp,code:newDisp.code.toUpperCase()});
    toast('Disposition added');
    api('misc.php?type=dispositions&action=list').then(r=>{if(r.status==='success')setDisps(r.data);});
    setNewDisp({code:'',label:'',category:'recent'});
  };

  const u=(k,v)=>setSettings(p=>({...p,[k]:v}));

  return (
    <div className="g2">
      <Card>
        <CT icon="⚙️">System Settings</CT>
        <Inp label="Office Hours Start" type="time" value={settings.office_start||''} onChange={e=>u('office_start',e.target.value)}/>
        <Inp label="Office Hours End" type="time" value={settings.office_end||''} onChange={e=>u('office_end',e.target.value)}/>
        <Inp label="Fresh Data Expiry (Hours)" type="number" value={settings.fresh_expiry_hours||48} onChange={e=>u('fresh_expiry_hours',e.target.value)}/>
        <Inp label="Break Yellow Alert (mins)" type="number" value={settings.break_yellow_mins||''} onChange={e=>u('break_yellow_mins',e.target.value)}/>
        <Inp label="Break Red Alert (mins)" type="number" value={settings.break_red_mins||''} onChange={e=>u('break_red_mins',e.target.value)}/>
        <Inp label="Idle Alert (mins)" type="number" value={settings.idle_alert_mins||''} onChange={e=>u('idle_alert_mins',e.target.value)}/>
        <Inp label="Default Daily Target" type="number" value={settings.default_daily_target||''} onChange={e=>u('default_daily_target',e.target.value)}/>
        <Inp label="Google Sheet URL (for Punch Orders)" value={settings.google_sheet_url||''} onChange={e=>u('google_sheet_url',e.target.value)} placeholder="https://script.google.com/..."/>
        <Btn color="green" full onClick={save}>Save Settings</Btn>
      </Card>
      <Card>
        <CT icon="📝">Disposition Codes</CT>
        {disps.map(d=>(
          <div key={d.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 0',borderBottom:'1px solid var(--card-border)'}}>
            <Badge color="blue">{d.code}</Badge>
            <span style={{flex:1,fontSize:13}}>{d.label}</span>
            <Badge color="gray">{d.category}</Badge>
            <Btn size="sm" color="ghost" onClick={async()=>{await api('misc.php?type=dispositions&action=toggle','PUT',{id:d.id,is_active:d.is_active?0:1});api('misc.php?type=dispositions&action=list').then(r=>{if(r.status==='success')setDisps(r.data);});}}>{d.is_active?'Disable':'Enable'}</Btn>
          </div>
        ))}
        <div style={{display:'flex',gap:8,marginTop:16,flexWrap:'wrap'}}>
          <input className="inp" value={newDisp.code} onChange={e=>setNewDisp({...newDisp,code:e.target.value})} placeholder="Code" style={{width:70}}/>
          <input className="inp" value={newDisp.label} onChange={e=>setNewDisp({...newDisp,label:e.target.value})} placeholder="Label" style={{flex:1}}/>
          <select className="sel" value={newDisp.category} onChange={e=>setNewDisp({...newDisp,category:e.target.value})} style={{width:120}}>
            <option value="recent">Recent</option>
            <option value="followup">Follow Up</option>
            <option value="converted">Converted</option>
          </select>
          <Btn color="green" onClick={addDisp}>Add</Btn>
        </div>
      </Card>
    </div>
  );
}

// ============================================================
// ADMIN REPORTS
// ============================================================
function AdminReports({ products }) {
  const [from, setFrom] = useState(new Date().toISOString().slice(0,8)+'01');
  const [to, setTo] = useState(new Date().toISOString().slice(0,10));
  const [report, setReport] = useState([]);
  const [dataPool, setDataPool] = useState('all');
  const [dataProduct, setDataProduct] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedData, setCopiedData] = useState(false);
  const [dataRows, setDataRows] = useState([]);

  const loadDataExport = async()=>{
    const r = await api(`reports.php?action=get_data_copy&pool=${dataPool}&product_id=${dataProduct}`);
    if(r.status==='success') setDataRows(r.data);
    else toast(r.message,'error');
  };

  const load = async()=>{
    const r=await api(`reports.php?action=admin_tl_report&from=${from}&to=${to}`);
    if(r.status==='success') setReport(r.data);
    else toast(r.message,'error');
  };

  const copyReport = ()=>{
    if(!report.length) return toast('Pehle Show Report karo','error');
    const header = 'TL Name\tTotal Leads\tConverted\tTeam Size\tPunch Orders';
    const rows = report.map(r=>`${r.tl}\t${r.total_leads}\t${r.converted}\t${r.team_size}\t${r.punch_orders}`);
    navigator.clipboard.writeText([header,...rows].join('\n')).then(()=>{
      setCopied(true);
      toast('Copied! Google Sheet mein paste karo','success');
      setTimeout(()=>setCopied(false),3000);
    });
  };

  const copyDataCSV = ()=>{
    if(!dataRows.length){ toast('Pehle Show Data karo','error'); return; }
    const header = Object.keys(dataRows[0]).join('\t');
    const body = dataRows.map(row=>Object.values(row).map(v=>v||'').join('\t')).join('\n');
    navigator.clipboard.writeText(header+'\n'+body).then(()=>{
      setCopiedData(true);
      toast(`${dataRows.length} rows copied! Sheet mein paste karo`,'success');
      setTimeout(()=>setCopiedData(false),3000);
    }).catch(()=>toast('Copy failed','error'));
  };

  return (
    <>
    <Card>
      <CT icon="📈">Admin Report — TL Wise</CT>
      <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap',alignItems:'flex-end'}}>
        <div><label className="flabel">From</label><input className="inp" type="date" value={from} onChange={e=>setFrom(e.target.value)} style={{width:160}}/></div>
        <div><label className="flabel">To</label><input className="inp" type="date" value={to} onChange={e=>setTo(e.target.value)} style={{width:160}}/></div>
        <Btn color="green" onClick={load}>Show Report</Btn>
        {report.length>0 && <Btn color="ghost" onClick={copyReport}>{copied?'✅ Copied!':'📋 Copy for Sheet'}</Btn>}
      </div>
      {report.length?(
        <>
          <div style={{fontSize:12,color:'var(--text3)',marginBottom:10}}>💡 "Copy for Sheet" se copy karo → Google Sheet mein paste karo</div>
          <table className="tbl">
            <thead><tr><th>TL Name</th><th>Total Leads</th><th>Converted</th><th>Team Size</th><th>Punch Orders</th></tr></thead>
            <tbody>{report.map((r,i)=>(
              <tr key={i}>
                <td style={{fontWeight:700}}>{r.tl}</td>
                <td>{r.total_leads}</td>
                <td style={{color:'var(--green)',fontWeight:700,fontSize:16}}>{r.converted}</td>
                <td>{r.team_size}</td>
                <td>{r.punch_orders}</td>
              </tr>
            ))}</tbody>
          </table>
        </>
      ):<Empty title="Click Show Report to load data"/>}
    </Card>

    <Card>
      <CT icon="📦">Data Export (with Status & History)</CT>
      <p style={{color:'var(--text3)',fontSize:13,marginBottom:16}}>Lead data copy karo — pool, product filter ke saath. Phir Excel/Sheet mein paste karo.</p>
      <div style={{display:'flex',gap:12,flexWrap:'wrap',alignItems:'flex-end',marginBottom:16}}>
        <div>
          <label className="flabel">Pool / Status</label>
          <select className="sel" value={dataPool} onChange={e=>setDataPool(e.target.value)} style={{width:160}}>
            <option value="all">All Data</option>
            <option value="fresh">Fresh</option>
            <option value="recent">Recent</option>
            <option value="followup">Follow Up</option>
            <option value="converted">Converted</option>
          </select>
        </div>
        <div>
          <label className="flabel">Product</label>
          <select className="sel" value={dataProduct} onChange={e=>setDataProduct(e.target.value)} style={{width:180}}>
            <option value="">All Products</option>
            {products.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <Btn color="green" onClick={loadDataExport}>Show Data</Btn>
        {dataRows.length>0 && <Btn color="ghost" onClick={copyDataCSV}>{copiedData?'✅ Copied!':'📋 Copy for Sheet'}</Btn>}
      </div>
      {dataRows.length>0&&(
        <>
          <div style={{fontSize:12,color:'var(--text3)',marginBottom:10}}>Showing {dataRows.length} records — Copy karo → Sheet mein paste karo</div>
          <div style={{overflowX:'auto',maxHeight:400,overflowY:'auto'}}>
            <table className="tbl">
              <thead><tr><th>Name</th><th>Phone</th><th>Product</th><th>Status</th><th>Attempts</th><th>Last Disposition</th><th>Last Remark</th><th>TL</th><th>Caller</th></tr></thead>
              <tbody>{dataRows.map((r,i)=>(
                <tr key={i}>
                  <td>{r.Customer_Name||'—'}</td>
                  <td style={{fontFamily:'var(--mono)',color:'var(--green)',fontWeight:700}}>{r.Phone}</td>
                  <td><Badge color="blue">{r.Product}</Badge></td>
                  <td><Badge color={r.Status==='converted'?'green':r.Status==='fresh'?'green':r.Status==='recent'?'orange':'yellow'}>{r.Status}</Badge></td>
                  <td style={{textAlign:'center'}}>{r.Attempts}</td>
                  <td>{r.Last_Disposition||'—'}</td>
                  <td style={{maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',color:'var(--text3)',fontSize:12}}>{r.Last_Remark||'—'}</td>
                  <td style={{color:'var(--text2)'}}>{r.Assigned_TL}</td>
                  <td style={{color:'var(--text2)'}}>{r.Assigned_Caller}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}
    </Card>
    </>
  );
}

// ============================================================
// TL - DASHBOARD
// ============================================================
function TLDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [callers, setCallers] = useState([]);
  const [approvals, setApprovals] = useState([]);

  const load = useCallback(async()=>{
    const [sr,cr,ar] = await Promise.all([
      api('reports.php?action=tl_dashboard'),
      api('users.php?action=live_status'),
      api('auth.php?action=pending_approvals'),
    ]);
    if(sr.status==='success') setStats(sr.data);
    if(cr.status==='success') setCallers(cr.data);
    if(ar.status==='success') setApprovals(ar.data);
  },[]);

  useEffect(()=>{ 
    load(); 
    const t=setInterval(load, 8000); // every 8 sec for approvals
    return()=>clearInterval(t); 
  },[load]);

  const respond = async(id,status)=>{
    const r=await api('auth.php?action=respond_approval','POST',{approval_id:id,status});
    if(r.status==='success'){toast(status==='approved'?'Approved! User can now login':'Denied');load();}
    else toast(r.message,'error');
  };

  const s = stats?.stats||{};
  const si = {active:{cls:'dot-green',label:'Active',badge:'green'},break_yellow:{cls:'dot-yellow',label:'On Break',badge:'yellow'},break_red:{cls:'dot-red',label:'Long Break',badge:'red'},idle:{cls:'dot-red',label:'Idle',badge:'red'}};

  return (
    <>
      {approvals.length>0&&(
        <div style={{position:'fixed',top:20,right:20,zIndex:999,display:'flex',flexDirection:'column',gap:10,maxHeight:'80vh',overflowY:'auto'}}>
          {approvals.map(ap=>(
            <div key={ap.id} className="approval-popup" style={{position:'relative',animation:'_slide-in .3s ease'}}>
              <div style={{fontWeight:700,marginBottom:4,color:'var(--text)',fontSize:14}}>🔔 Login Request</div>
              <div style={{color:'var(--text2)',fontSize:13,marginBottom:4}}><strong>{ap.name}</strong> ({ap.role}) wants to login</div>
              <div style={{color:'var(--text3)',fontSize:11,marginBottom:12}}>
                {new Date(ap.requested_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true})}
              </div>
              <div style={{display:'flex',gap:8}}>
                <Btn color="green" size="sm" onClick={()=>respond(ap.id,'approved')}>✓ Approve</Btn>
                <Btn color="red" size="sm" onClick={()=>respond(ap.id,'denied')}>✕ Deny</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="stat-grid">
        <StatCard val={s.fresh_available||0} label="Fresh — Available" icon="🌱" bg="rgba(0,200,83,0.15)" ac="linear-gradient(90deg,#00c853,#69f0ae)"/>
        <StatCard val={s.fresh_assigned||0} label="Fresh — With Callers" icon="📤" bg="rgba(33,150,243,0.15)" ac="linear-gradient(90deg,#2196f3,#42a5f5)"/>
        <StatCard val={s.converted||0} label="Total Converted" icon="✅" bg="rgba(255,107,43,0.15)" ac="linear-gradient(90deg,#ff6b2b,#ff9a3c)"/>
        <StatCard val={stats?.missed_followups||0} label="Missed Follow Ups" icon="⚠️" bg="rgba(244,67,54,0.15)" ac="linear-gradient(90deg,#f44336,#e53935)"/>
      </div>

      <Card>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <CT icon="👁️">Team Live Status</CT>
          <Btn color="ghost" size="sm" onClick={load}>↻ Refresh</Btn>
        </div>
        <table className="tbl">
          <thead><tr><th>Caller</th><th>Status</th><th>Today Calls</th><th>Last Attempt</th><th>Idle (min)</th></tr></thead>
          <tbody>
            {callers.map(c=>{
              const info=si[c.status]||{cls:'dot-gray',label:'Unknown',badge:'gray'};
              const idle=parseInt(c.idle_minutes)||0;
              return(
                <tr key={c.id}>
                  <td style={{fontWeight:600}}>{c.name}</td>
                  <td><span className={`dot ${info.cls}`} style={{marginRight:6}}/><Badge color={info.badge}>{info.label}</Badge></td>
                  <td style={{fontWeight:700,color:'var(--green)'}}>{c.today_attempts||0}</td>
                  <td style={{color:'var(--text3)',fontSize:12,fontFamily:'var(--mono)'}}>{c.last_attempt?.slice(11,16)||'—'}</td>
                  <td style={{fontWeight:700,color:idle>20?'var(--red)':idle>10?'var(--yellow)':'var(--green)'}}>{idle}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!callers.length && <Empty title="No callers in your team"/>}
      </Card>
    </>
  );
}

// ============================================================
// TL - MY CALLERS
// ============================================================
function TLCallers({ user }) {
  const [callers, setCallers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name:'', email:'', password:'Pass@123', is_exempt:false });
  const [noteModal, setNoteModal] = useState(null);
  const [note, setNote] = useState('');
  const [targets, setTargets] = useState({});
  const [editTarget, setEditTarget] = useState(null);
  const [targetVal, setTargetVal] = useState(5);

  const load = async()=>{
    const r=await api('users.php?action=list');
    if(r.status==='success') setCallers(r.data.filter(c=>c.role==='caller'&&String(c.tl_id)===String(user.id)));
    const tr=await api('users.php?action=get_targets');
    if(tr.status==='success'){
      const t={};
      tr.data.forEach(x=>t[x.id]=x.daily_target);
      setTargets(t);
    }
  };
  useEffect(()=>{load();},[]);

  const saveTarget = async()=>{
    const r=await api('users.php?action=set_target','POST',{caller_id:editTarget,target:targetVal});
    if(r.status==='success'){toast('Target set!');setEditTarget(null);load();}
    else toast(r.message,'error');
  };

  const create = async()=>{
    const r=await api('users.php?action=create','POST',{...form,role:'caller',is_exempt:form.is_exempt?1:0});
    if(r.status==='success'){toast('Caller created! Password: '+form.password);setShowCreate(false);load();setForm({name:'',email:'',password:'Pass@123',is_exempt:false});}
    else toast(r.message,'error');
  };

  const openNote = async(c)=>{
    setNoteModal(c);
    const r=await api(`users.php?action=get_note&caller_id=${c.id}`);
    if(r.status==='success') setNote(r.data.note);
  };

  const saveNote = async()=>{
    await api('users.php?action=save_note','POST',{caller_id:noteModal.id,note});
    toast('Note saved');setNoteModal(null);
  };

  return (
    <>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div style={{color:'var(--text2)'}}>{callers.length} callers in your team</div>
        <Btn color="green" onClick={()=>setShowCreate(true)}>+ Add Caller</Btn>
      </div>
      <Card>
        <table className="tbl">
          <thead><tr><th>Name</th><th>Email</th><th>Daily Target</th><th>Status</th><th>Last Login</th><th>Actions</th></tr></thead>
          <tbody>
            {callers.map(c=>(
              <tr key={c.id}>
                <td style={{fontWeight:600}}>{c.name}</td>
                <td style={{color:'var(--text3)',fontSize:12}}>{c.email}</td>
                <td>
                  <span style={{fontWeight:700,color:'var(--orange)',fontSize:15}}>{targets[c.id]||5}</span>
                  <Btn size="sm" color="ghost" style={{marginLeft:8}} onClick={()=>{setEditTarget(c.id);setTargetVal(targets[c.id]||5);}}>Set</Btn>
                </td>
                <td><Badge color={c.is_active?'green':'red'}>{c.is_active?'Active':'Inactive'}</Badge></td>
                <td style={{color:'var(--text3)',fontSize:12,fontFamily:'var(--mono)'}}>{c.last_login?.slice(0,16)||'Never'}</td>
                <td style={{display:'flex',gap:6}}>
                  <Btn size="sm" color="ghost" onClick={()=>openNote(c)}>📝 Note</Btn>
                  <Btn size="sm" color={c.is_active?'red':'green'} onClick={async()=>{await api('users.php?action=update','PUT',{id:c.id,is_active:c.is_active?0:1});toast('Updated');load();}}>{c.is_active?'Deactivate':'Activate'}</Btn>
                  <Btn size="sm" color="red" onClick={async()=>{
                    if(!window.confirm(`Delete ${c.name}? Their leads will return to your pool.`)) return;
                    const r=await api('users.php?action=delete','DELETE',{id:c.id});
                    if(r.status==='success'){toast('Deleted. Leads returned to your pool.','info');load();}
                    else toast(r.message,'error');
                  }}>Delete</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!callers.length && <Empty title="No callers yet" desc="Add callers to your team"/>}
      </Card>

      <div className={`modal-bg${showCreate?' open':''}`} onClick={e=>e.target===e.currentTarget&&setShowCreate(false)}>
        <div className="modal">
          <div className="modal-title">Add New Caller</div>
          <Inp label="Full Name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>
          <Inp label="Email" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
          <Inp label="Password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})}/>
          <label style={{display:'flex',alignItems:'center',gap:8,color:'var(--text2)',fontSize:13,cursor:'pointer',marginBottom:20}}>
            <input type="checkbox" checked={form.is_exempt} onChange={e=>setForm({...form,is_exempt:e.target.checked})}/>
            Exempt from login approval
          </label>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <Btn color="ghost" onClick={()=>setShowCreate(false)}>Cancel</Btn>
            <Btn color="green" onClick={create}>Create Caller</Btn>
          </div>
        </div>
      </div>

      <div className={`modal-bg${noteModal?' open':''}`} onClick={e=>e.target===e.currentTarget&&setNoteModal(null)}>
        <div className="modal">
          <div className="modal-title">Private Note — {noteModal?.name}</div>
          <TA label="Note (only you can see this)" value={note} onChange={e=>setNote(e.target.value)} rows={6} placeholder="Performance notes, observations..."/>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:16}}>
            <Btn color="ghost" onClick={()=>setNoteModal(null)}>Cancel</Btn>
            <Btn color="green" onClick={saveNote}>Save Note</Btn>
          </div>
        </div>
      </div>

      {/* TARGET MODAL */}
      <div className={`modal-bg${editTarget?' open':''}`} onClick={e=>e.target===e.currentTarget&&setEditTarget(null)}>
        <div className="modal" style={{maxWidth:320}}>
          <div className="modal-title">🎯 Set Daily Target</div>
          <p style={{color:'var(--text3)',fontSize:13,marginBottom:16}}>Set daily conversion target for {callers.find(c=>c.id==editTarget)?.name}</p>
          <Inp label="Daily Target (conversions)" type="number" value={targetVal} onChange={e=>setTargetVal(parseInt(e.target.value)||1)} min={1} max={100}/>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:16}}>
            <Btn color="ghost" onClick={()=>setEditTarget(null)}>Cancel</Btn>
            <Btn color="orange" onClick={saveTarget}>Save Target</Btn>
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================
// TL REPORTS
// ============================================================
function TLReport({ products }) {
  const [report, setReport] = useState([]);
  const [from, setFrom] = useState(new Date().toISOString().slice(0,8)+'01');
  const [to, setTo] = useState(new Date().toISOString().slice(0,10));
  const [productId, setProductId] = useState('');
  const [copied, setCopied] = useState(false);

  const load = async()=>{
    const r=await api(`reports.php?action=team&from=${from}&to=${to}&product_id=${productId}`);
    if(r.status==='success') setReport(r.data);
    else toast(r.message,'error');
  };

  const copyTable = ()=>{
    if(!report.length) return toast('No data to copy','error');
    const header = 'Caller\tTotal Calls\tConverted\tFollow Ups\tNot Connected\tFresh\tRecent\tPerformance';
    const rows = report.map(r=>`${r.name}\t${r.total_calls}\t${r.converted}\t${r.followups}\t${r.not_connected}\t${r.fresh_leads}\t${r.recent_leads}\t${r.color_label}`);
    const text = [header,...rows].join('\n');
    navigator.clipboard.writeText(text).then(()=>{
      setCopied(true);
      toast('Copied! Paste in Google Sheet or Excel','success');
      setTimeout(()=>setCopied(false),3000);
    });
  };

  const colorClass = {green:'tbl-row-green',yellow:'tbl-row-yellow',red:'tbl-row-red'};
  const colorBadge = {green:'green',yellow:'yellow',red:'red'};

  return (
    <Card>
      <CT icon="📊">Team Performance Report</CT>
      <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap',alignItems:'flex-end'}}>
        <div><label className="flabel">From</label><input className="inp" type="date" value={from} onChange={e=>setFrom(e.target.value)} style={{width:160}}/></div>
        <div><label className="flabel">To</label><input className="inp" type="date" value={to} onChange={e=>setTo(e.target.value)} style={{width:160}}/></div>
        <div><label className="flabel">Product</label><select className="sel" value={productId} onChange={e=>setProductId(e.target.value)} style={{width:180}}>
          <option value="">All Products</option>
          {products.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select></div>
        <Btn color="green" onClick={load}>Show Report</Btn>
        {report.length>0 && <Btn color="ghost" onClick={copyTable}>{copied?'✅ Copied!':'📋 Copy for Sheet'}</Btn>}
      </div>
      {report.length?(
        <>
          <div style={{fontSize:12,color:'var(--text3)',marginBottom:10}}>💡 "Copy for Sheet" button se data copy karo → Google Sheet mein paste karo</div>
          <table className="tbl">
            <thead><tr><th>Caller</th><th>Total Calls</th><th>Converted</th><th>Follow Ups</th><th>Not Connected</th><th>Fresh</th><th>Recent</th><th>Performance</th></tr></thead>
            <tbody>{report.map(r=>(
              <tr key={r.id} className={colorClass[r.color]}>
                <td style={{fontWeight:700}}>{r.name}</td>
                <td>{r.total_calls}</td>
                <td style={{fontWeight:800,fontSize:17,color:'var(--green)'}}>{r.converted}</td>
                <td>{r.followups}</td>
                <td>{r.not_connected}</td>
                <td>{r.fresh_leads}</td>
                <td>{r.recent_leads}</td>
                <td><Badge color={colorBadge[r.color]}>{r.color_label}</Badge></td>
              </tr>
            ))}</tbody>
          </table>
        </>
      ):<Empty title="Click Show Report to load data"/>}
    </Card>
  );
}


// ============================================================
// FOLLOW UPS
// ============================================================
function FollowUps({ user }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reassignModal, setReassignModal] = useState(null);
  const [callers, setCallers] = useState([]);
  const [newCaller, setNewCaller] = useState('');
  const [punchLead, setPunchLead] = useState(null);
  const [remarkLead, setRemarkLead] = useState(null);
  const [punchForm, setPunchForm] = useState({customer_name:'',phone:'',address:'',city:'',product_id:'',quantity:1,price:'',remark:''});
  const [remarkForm, setRemarkForm] = useState({disp:'',remark:'',followup_at:''});
  const [products, setProducts] = useState([]);
  const [punching, setPunching] = useState(false);
  const [remarking, setRemarking] = useState(false);

  const load = async()=>{
    setLoading(true);
    const r=await api('leads.php?action=followups');
    if(r.status==='success') setLeads(r.data);
    setLoading(false);
  };

  useEffect(()=>{
    load();
    api('misc.php?type=products&action=list').then(r=>{if(r.status==='success')setProducts(r.data);});
    if(user.role==='tl') api('users.php?action=list').then(r=>{if(r.status==='success')setCallers(r.data);});
  },[user]);

  const openPunch = (l)=>{
    setPunchLead(l);
    setPunchForm({customer_name:l.name||'',phone:l.phone||'',address:'',city:'',product_id:'',quantity:1,price:'',remark:''});
  };

  const openRemark = (l)=>{
    setRemarkLead(l);
    setRemarkForm({disp:'',remark:'',followup_at:''});
  };

  const submitPunch = async()=>{
    if(!punchForm.customer_name||!punchForm.phone||!punchForm.city||!punchForm.product_id||!punchForm.price)
      return toast('Name, phone, city, product and price required','error');
    setPunching(true);
    const r=await api('punch.php?action=create','POST',{...punchForm,lead_id:punchLead.id});
    if(r.status==='success'){toast(r.message);setPunchLead(null);load();}
    else toast(r.message,'error');
    setPunching(false);
  };

  const submitRemark = async()=>{
    if(!remarkForm.disp) return toast('Select a disposition','error');
    if(remarkForm.disp==='FU'&&!remarkForm.followup_at) return toast('Follow up date/time required','error');
    setRemarking(true);
    const r=await api('leads.php?action=dispose','POST',{
      lead_id:remarkLead.id,
      disposition:remarkForm.disp,
      remark:remarkForm.remark,
      followup_at:remarkForm.followup_at||null
    });
    if(r.status==='success'){toast('Remark saved!');setRemarkLead(null);load();}
    else toast(r.message,'error');
    setRemarking(false);
  };

  const reassign = async()=>{
    if(!newCaller) return toast('Select a caller','error');
    const r=await api('leads.php?action=reassign_followup','POST',{lead_id:reassignModal.id,caller_id:newCaller});
    if(r.status==='success'){toast('Follow up reassigned');setReassignModal(null);load();}
    else toast(r.message,'error');
  };

  const dispConfig = [
    {code:'NC',label:'Not Connected'},{code:'SW',label:'Switch Off'},{code:'BZ',label:'Busy'},
    {code:'NP',label:'Not Picked'},{code:'OFN',label:'Out of Network'},{code:'CB',label:'Call Back'},
    {code:'NI',label:'Not Interested'},{code:'FU',label:'Follow Up Again'},{code:'CUSTOM',label:'Custom'},
  ];

  const now = new Date();

  return (
    <>
    <Card>
      <CT icon="📅">Follow Ups{leads.length>0&&<Badge color="orange" style={{marginLeft:8}}>{leads.length}</Badge>}</CT>
      {loading?<Spin/>:leads.length===0?<Empty title="No follow ups pending"/>:
        leads.map(l=>{
          const due=new Date(l.followup_at);
          const overdue=due<now;
          const soon=!overdue&&(due-now)<300000;
          return(
            <div key={l.id} className={`fu-card${overdue?' overdue':soon?' soon':''}`}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                <div>
                  <div style={{fontWeight:700,fontSize:15}}>{l.name||l.phone}</div>
                  <div style={{fontFamily:'var(--mono)',color:'var(--green)',fontWeight:700,fontSize:16,margin:'4px 0'}}>{l.phone}</div>
                  <div style={{color:'var(--text3)',fontSize:12}}>{l.product_name}</div>
                  {user.role==='tl'&&l.caller_name&&<div style={{color:'var(--text3)',fontSize:12}}>Caller: {l.caller_name}</div>}
                  {l.last_remark&&<div style={{color:'var(--text2)',fontSize:13,marginTop:4,fontStyle:'italic'}}>"{l.last_remark}"</div>}
                </div>
                <div style={{textAlign:'right'}}>
                  <Badge color={overdue?'red':soon?'orange':'green'}>{overdue?'Overdue':soon?'Due Soon':'Upcoming'}</Badge>
                  <div style={{color:'var(--text3)',fontSize:12,marginTop:6,fontFamily:'var(--mono)'}}>{l.followup_at?.slice(0,16)}</div>
                </div>
              </div>
              {user.role==='caller'&&(
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  <Btn size="sm" color="orange" onClick={()=>openPunch(l)}>📋 Punch Order</Btn>
                  <Btn size="sm" color="ghost" onClick={()=>openRemark(l)}>✏️ Add Remark</Btn>
                </div>
              )}
              {user.role==='tl'&&(
                <div style={{display:'flex',gap:8,marginTop:8}}>
                  <Btn size="sm" color="ghost" onClick={()=>setReassignModal(l)}>Reassign</Btn>
                </div>
              )}
            </div>
          );
        })
      }
    </Card>

    {/* PUNCH MODAL */}
    <div className={`modal-bg${punchLead?' open':''}`} onClick={e=>e.target===e.currentTarget&&setPunchLead(null)}>
      <div className="modal">
        <div className="modal-title">📋 Punch Order — {punchLead?.name||punchLead?.phone}</div>
        <div className="g2">
          <Inp label="Customer Name" value={punchForm.customer_name} onChange={e=>setPunchForm({...punchForm,customer_name:e.target.value})}/>
          <Inp label="Phone Number" value={punchForm.phone} onChange={e=>setPunchForm({...punchForm,phone:e.target.value})}/>
        </div>
        <TA label="Address (Optional)" value={punchForm.address} onChange={e=>setPunchForm({...punchForm,address:e.target.value})} rows={2} placeholder="House no, Street, Area..."/>
        <div className="g2">
          <Inp label="City" value={punchForm.city} onChange={e=>setPunchForm({...punchForm,city:e.target.value})}/>
          <Sel label="Product" value={punchForm.product_id} onChange={e=>setPunchForm({...punchForm,product_id:e.target.value})}>
            <option value="">-- Select --</option>
            {products.map(p=><option key={p.id} value={p.id}>{p.name} (Min ₹{p.min_price})</option>)}
          </Sel>
        </div>
        <div className="g2">
          <Inp label="Quantity" type="number" value={punchForm.quantity} onChange={e=>setPunchForm({...punchForm,quantity:e.target.value})} min={1}/>
          <Inp label="Price (₹)" type="number" value={punchForm.price} onChange={e=>setPunchForm({...punchForm,price:e.target.value})}/>
        </div>
        <TA label="Remark (Optional)" value={punchForm.remark} onChange={e=>setPunchForm({...punchForm,remark:e.target.value})} rows={2}/>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:16}}>
          <Btn color="ghost" onClick={()=>setPunchLead(null)}>Cancel</Btn>
          <Btn color="orange" onClick={submitPunch} disabled={punching}>{punching?'Submitting...':'Submit Punch Order'}</Btn>
        </div>
      </div>
    </div>

    {/* REMARK MODAL */}
    <div className={`modal-bg${remarkLead?' open':''}`} onClick={e=>e.target===e.currentTarget&&setRemarkLead(null)}>
      <div className="modal">
        <div className="modal-title">✏️ Add Remark — {remarkLead?.name||remarkLead?.phone}</div>
        <div style={{fontFamily:'var(--mono)',color:'var(--green)',fontWeight:700,fontSize:18,marginBottom:16}}>{remarkLead?.phone}</div>
        <div className="flabel" style={{marginBottom:8}}>Disposition</div>
        <div className="disp-grid" style={{marginBottom:16}}>
          {dispConfig.map(d=>(
            <button key={d.code} className={`disp-btn${remarkForm.disp===d.code?' dsel-green':''}`} onClick={()=>setRemarkForm({...remarkForm,disp:d.code})}>{d.label}</button>
          ))}
          <button className={`disp-btn${remarkForm.disp==='PUNCH'?' dsel-orange':''}`} onClick={()=>{setRemarkLead(null);openPunch(remarkLead);}}>📋 Punch Order</button>
        </div>
        {remarkForm.disp==='FU'&&<Inp label="Follow Up Date & Time" type="datetime-local" value={remarkForm.followup_at} onChange={e=>setRemarkForm({...remarkForm,followup_at:e.target.value})}/>}
        <TA label="Remark" value={remarkForm.remark} onChange={e=>setRemarkForm({...remarkForm,remark:e.target.value})} rows={3} placeholder="What happened on this call..."/>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:16}}>
          <Btn color="ghost" onClick={()=>setRemarkLead(null)}>Cancel</Btn>
          <Btn color="green" onClick={submitRemark} disabled={remarking||!remarkForm.disp}>{remarking?'Saving...':'Save Remark'}</Btn>
        </div>
      </div>
    </div>

    {/* REASSIGN MODAL */}
    <div className={`modal-bg${reassignModal?' open':''}`} onClick={e=>e.target===e.currentTarget&&setReassignModal(null)}>
      <div className="modal">
        <div className="modal-title">Reassign Follow Up</div>
        <p style={{color:'var(--text2)',fontSize:13,marginBottom:16}}>Reassign <strong>{reassignModal?.name||reassignModal?.phone}</strong>'s follow up</p>
        <Sel label="Assign to Caller" value={newCaller} onChange={e=>setNewCaller(e.target.value)}>
          <option value="">-- Select Caller --</option>
          {callers.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
        </Sel>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:16}}>
          <Btn color="ghost" onClick={()=>setReassignModal(null)}>Cancel</Btn>
          <Btn color="orange" onClick={reassign}>Reassign</Btn>
        </div>
      </div>
    </div>
    </>
  );
}


// ============================================================
// MESSAGES
// ============================================================
function Messages({ user }) {
  const [msgs, setMsgs] = useState([]);
  const [callers, setCallers] = useState([]);
  const [msg, setMsg] = useState('');
  const [toId, setToId] = useState('');
  const [broadcast, setBroadcast] = useState(false);

  const loadMsgs = async()=>{
    const r=await api('misc.php?type=messages&action=inbox');
    if(r.status==='success') setMsgs(r.data);
  };

  useEffect(()=>{
    loadMsgs();
    if(user.role!=='caller') api('users.php?action=list').then(r=>{if(r.status==='success')setCallers(r.data.filter(u=>u.role==='caller'));});
  },[user]);

  const send = async()=>{
    if(!msg.trim()) return toast('Enter a message','error');
    const r=await api('misc.php?type=messages&action=send','POST',{to_id:broadcast?null:toId||null,message:msg,is_broadcast:broadcast?1:0});
    if(r.status==='success'){toast('Message sent!');setMsg('');loadMsgs();}
    else toast(r.message,'error');
  };

  return (
    <>
      {user.role!=='caller'&&(
        <Card>
          <CT icon="📤">Send Message</CT>
          <label style={{display:'flex',alignItems:'center',gap:8,color:'var(--text2)',fontSize:13,cursor:'pointer',marginBottom:14}}>
            <input type="checkbox" checked={broadcast} onChange={e=>setBroadcast(e.target.checked)}/>
            Broadcast to entire team
          </label>
          {!broadcast&&(
            <Sel label="Select Caller" value={toId} onChange={e=>setToId(e.target.value)}>
              <option value="">-- Select --</option>
              {callers.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
            </Sel>
          )}
          <div style={{display:'flex',gap:10}}>
            <input className="inp" value={msg} onChange={e=>setMsg(e.target.value)} placeholder="Type message..." style={{flex:1}} onKeyDown={e=>e.key==='Enter'&&send()}/>
            <Btn color="green" onClick={send}>Send</Btn>
          </div>
        </Card>
      )}
      <Card>
        <CT icon="📥">Inbox</CT>
        {msgs.length===0?<Empty title="No messages"/>:
          msgs.map(m=>(
            <div key={m.id} className={`msg-item${!m.is_read?' unread':''}`}>
              <div style={{fontWeight:700,color:'var(--green)',marginBottom:4}}>{m.from_name}{m.is_broadcast&&' (Broadcast)'}</div>
              <div style={{fontSize:13,lineHeight:1.6}}>{m.message}</div>
              <div style={{color:'var(--text3)',fontSize:11,marginTop:5,fontFamily:'var(--mono)'}}>{m.created_at?.slice(0,16)}</div>
            </div>
          ))
        }
      </Card>
    </>
  );
}

// ============================================================
// CALLER - CALLING SCREEN
// ============================================================
function CallingScreen({ user }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disp, setDisp] = useState('');
  const [remark, setRemark] = useState('');
  const [followupAt, setFollowupAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showScript, setShowScript] = useState(false);
  const [showPunch, setShowPunch] = useState(false);
  const [punchForm, setPunchForm] = useState({ customer_name:'', phone:'', address:'', city:'', product_id:'', quantity:1, price:'', remark:'' });
  const [products, setProducts] = useState([]);
  const [punching, setPunching] = useState(false);
  const [todayStats, setTodayStats] = useState(null);
  const [tab, setTab] = useState('fresh');
  const [freshLeads, setFreshLeads] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const loadLeads = useCallback(async()=>{
    setListLoading(true);
    const [fr, re] = await Promise.all([
      api('leads.php?action=my_leads&pool=fresh&search='),
      api('leads.php?action=my_leads&pool=recent&search=')
    ]);
    // IST today date
    const nowIST = new Date(Date.now() + (5.5*60*60*1000));
    const todayIST = nowIST.toISOString().slice(0,10);
    // Filter out leads attempted today
    const notToday = l => !l.last_attempt_at || l.last_attempt_at.slice(0,10) !== todayIST;
    if(fr.status==='success') setFreshLeads(fr.data.filter(notToday));
    if(re.status==='success') setRecentLeads(re.data.filter(notToday));
    setListLoading(false);
  },[]);

  const fetchLead = useCallback(async(leadId=null)=>{
    setLoading(true);
    let r;
    if(leadId){
      r = await api(`leads.php?action=get_lead&id=${leadId}`);
    } else {
      r = await api('leads.php?action=next');
    }
    if(r.status==='success'){
      if(r.data && r.data.id) setLead(r.data);
      else setLead(null);
    } else setLead(null);
    setLoading(false);
    setDisp('');setRemark('');setFollowupAt('');setShowScript(false);setShowPunch(false);
    setPunchForm({customer_name:'',phone:'',address:'',city:'',product_id:'',quantity:1,price:'',remark:''});
  },[]);

  useEffect(()=>{
    fetchLead();
    loadLeads();
    api('misc.php?type=products&action=list').then(r=>{if(r.status==='success')setProducts(r.data);});
    api('reports.php?action=personal').then(r=>{if(r.status==='success')setTodayStats(r.data);});
  },[fetchLead, loadLeads]);

  const submit = async()=>{
    if(!disp) return toast('Select a disposition','error');
    if(disp==='FU'&&!followupAt) return toast('Follow up date/time required','error');
    setSubmitting(true);
    const doneId = lead.id;
    const r=await api('leads.php?action=dispose','POST',{lead_id:lead.id,disposition:disp,remark,followup_at:followupAt||null});
    if(r.status==='success'){
      toast('Saved!');
      setDisp('');setRemark('');setFollowupAt('');
      setFreshLeads(prev=>prev.filter(l=>l.id!==doneId));
      setRecentLeads(prev=>prev.filter(l=>l.id!==doneId));
      fetchLead();
      api('reports.php?action=personal').then(r=>{if(r.status==='success')setTodayStats(r.data);});
    } else toast(r.message,'error');
    setSubmitting(false);
  };

  const submitPunch = async()=>{
    if(!punchForm.customer_name||!punchForm.phone||!punchForm.city||!punchForm.product_id||!punchForm.price)
      return toast('Customer name, phone, city, product and price required','error');
    setPunching(true);
    const r=await api('punch.php?action=create','POST',{...punchForm,lead_id:lead.id});
    if(r.status==='success'){
      toast(r.message);
      setShowPunch(false);
      setPunchForm({customer_name:'',phone:'',address:'',city:'',product_id:'',quantity:1,price:'',remark:''});
      // Remove from calling list + load next
      const doneId = lead.id;
      setFreshLeads(prev=>prev.filter(l=>l.id!==doneId));
      setRecentLeads(prev=>prev.filter(l=>l.id!==doneId));
      fetchLead();
    } else toast(r.message,'error');
    setPunching(false);
  };

  const [showVerbalPunch, setShowVerbalPunch] = useState(false);
  const [verbalForm, setVerbalForm] = useState({customer_name:'',phone:'',address:'',city:'',product_id:'',quantity:1,price:'',remark:''});
  const [verbaling, setVerbaling] = useState(false);

  const submitVerbal = async()=>{
    if(!verbalForm.customer_name||!verbalForm.phone||!verbalForm.city||!verbalForm.product_id||!verbalForm.price)
      return toast('All fields required','error');
    setVerbaling(true);
    const r=await api('punch.php?action=custom_punch','POST',verbalForm);
    if(r.status==='success'){
      toast(r.message);
      setShowVerbalPunch(false);
      setVerbalForm({customer_name:'',phone:'',address:'',city:'',product_id:'',quantity:1,price:'',remark:''});
    } else toast(r.message,'error');
    setVerbaling(false);
  };

  const markConverted = async()=>{
    if(!lead?.punch_owner_id||lead.punch_owner_id!=user.id) return toast('You must punch this order first before marking converted','error');
    const r=await api('leads.php?action=mark_converted','POST',{lead_id:lead.id});
    if(r.status==='success'){toast('Lead marked as converted!');fetchLead();}
    else toast(r.message,'error');
  };

  const dispConfig = [
    {code:'NC',label:'Not Connected',cls:''},
    {code:'SW',label:'Switch Off',cls:''},
    {code:'BZ',label:'Busy',cls:''},
    {code:'NP',label:'Not Picked',cls:''},
    {code:'OFN',label:'Out of Network',cls:''},
    {code:'CB',label:'Call Back',cls:''},
    {code:'NI',label:'Not Interested',cls:'dsel-red'},
    {code:'FU',label:'Follow Up',cls:'dsel-orange'},
    {code:'CUSTOM',label:'Custom Remark',cls:''},
  ];

  if(loading) return <Spin/>;

  if(!lead||!lead.id) return (
    <Card>
      <div style={{textAlign:'center',padding:'60px 24px'}}>
        <div style={{fontSize:64,marginBottom:16}}>✅</div>
        <h2 style={{fontFamily:'Poppins,sans-serif',color:'var(--green)',marginBottom:8,fontSize:22}}>You're all caught up!</h2>
        <p style={{color:'var(--text2)',fontSize:15,marginBottom:6,fontWeight:500}}>No pending leads at the moment.</p>
        <p style={{color:'var(--text3)',fontSize:13,marginBottom:28,lineHeight:1.7}}>You have completed all your assigned leads.<br/>Please contact your Team Lead to assign more leads.</p>
        <div style={{background:'rgba(0,200,83,0.08)',border:'1px solid rgba(0,200,83,0.2)',borderRadius:'var(--r)',padding:'14px 20px',marginBottom:24,display:'inline-block'}}>
          <span style={{color:'var(--green)',fontWeight:600,fontSize:13}}>💡 Check your Follow Ups — they may still need attention</span>
        </div>
        <div style={{display:'flex',gap:12,justifyContent:'center'}}>
          <Btn color="green" onClick={fetchLead}>↻ Refresh</Btn>
        </div>
      </div>
    </Card>
  );

  return (
    <>
      {/* Target Progress Banner */}
      {todayStats && (()=>{
        const converted = todayStats.today?.converted||0;
        const target = todayStats.daily_target||5;
        const progress = Math.min(100, Math.round(converted/target*100));
        const color = progress>=100?'var(--green)':progress>=60?'var(--orange)':'var(--red)';
        return (
          <div style={{background:'var(--card)',border:'1px solid var(--card-border)',borderRadius:'var(--r)',padding:'12px 18px',marginBottom:16,display:'flex',alignItems:'center',gap:16}}>
            <span style={{fontSize:20}}>🎯</span>
            <div style={{flex:1}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                <span style={{fontWeight:600,fontSize:13,color:'var(--text)'}}>Today's Target</span>
                <span style={{fontWeight:800,color,fontSize:14}}>{converted}/{target} ({progress}%)</span>
              </div>
              <div style={{background:'rgba(0,0,0,0.08)',borderRadius:6,height:8,overflow:'hidden'}}>
                <div style={{height:'100%',width:`${progress}%`,background:color,borderRadius:6,transition:'width .5s'}}/>
              </div>
            </div>
            {progress>=100&&<span style={{fontSize:20}}>🎉</span>}
          </div>
        );
      })()}

      <div className="g2" style={{alignItems:'start'}}>
        {/* LEFT — Lead list tabs */}
        <Card style={{marginBottom:0}}>
          <div className="tabs" style={{marginBottom:12}}>
            <button className={`tab${tab==='fresh'?' active':''}`} onClick={()=>setTab('fresh')}>
              🌱 Fresh <span style={{marginLeft:4,background:'rgba(0,200,83,0.15)',color:'var(--green)',borderRadius:10,padding:'1px 7px',fontSize:11,fontWeight:700}}>{freshLeads.length}</span>
            </button>
            <button className={`tab${tab==='recent'?' active':''}`} onClick={()=>setTab('recent')}>
              🔄 Recent <span style={{marginLeft:4,background:'rgba(255,107,43,0.15)',color:'var(--orange)',borderRadius:10,padding:'1px 7px',fontSize:11,fontWeight:700}}>{recentLeads.length}</span>
            </button>
          </div>
          {listLoading ? <Spin/> : (
            <div style={{maxHeight:420,overflowY:'auto'}}>
              {(tab==='fresh'?freshLeads:recentLeads).length===0
                ? <Empty title={`No ${tab} leads`} desc="TL se aur leads maango"/>
                : (tab==='fresh'?freshLeads:recentLeads).map(l=>(
                  <div key={l.id} onClick={()=>fetchLead(l.id)}
                    style={{padding:'10px 12px',borderRadius:'var(--r-sm)',border:`1.5px solid ${lead?.id===l.id?'var(--green)':'var(--card-border)'}`,background:lead?.id===l.id?'rgba(0,200,83,0.08)':'transparent',marginBottom:6,cursor:'pointer',transition:'all .15s'}}>
                    <div style={{fontWeight:700,fontSize:13}}>{l.name||'Unknown'}</div>
                    <div style={{fontFamily:'var(--mono)',color:'var(--green)',fontWeight:700,fontSize:15}}>{l.phone}</div>
                    <div style={{display:'flex',gap:8,marginTop:4,alignItems:'center'}}>
                      <Badge color={l.pool==='fresh'?'green':'orange'} style={{fontSize:10}}>{l.pool}</Badge>
                      <Badge color="blue" style={{fontSize:10}}>{l.product_name}</Badge>
                    </div>
                    {l.last_remark&&<div style={{fontSize:11,color:'var(--text3)',marginTop:3,fontStyle:'italic',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>"{l.last_remark}"</div>}
                  </div>
                ))
              }
            </div>
          )}
        </Card>

        {/* RIGHT — Calling area */}
        <div>
          {loading ? <Spin/> : !lead||!lead.id ? (
            <Card>
              <div style={{textAlign:'center',padding:'40px 24px'}}>
                <div style={{fontSize:56,marginBottom:12}}>✅</div>
                <h3 style={{fontFamily:'Poppins,sans-serif',color:'var(--green)',marginBottom:8}}>You're all caught up!</h3>
                <p style={{color:'var(--text3)',fontSize:13,marginBottom:16}}>Select a lead from the left panel<br/>or ask your TL for more leads.</p>
                <Btn color="green" size="sm" onClick={()=>{fetchLead();loadLeads();}}>↻ Refresh</Btn>
              </div>
            </Card>
          ) : (
            <>
              {/* Lead Hero Card */}
              <div className={`lead-hero${lead.is_priority?' priority':''}`}>
                {lead.is_priority&&<div style={{marginBottom:8}}><Badge color="orange">⭐ Priority Lead</Badge></div>}
                <div className="lead-pending">{lead.pending} pending</div>
                <div className="lead-product">{lead.product_name}</div>
                <div className="lead-name">{lead.name||'Name not available'}</div>
                <div className="lead-phone">{lead.phone}</div>
                {lead.last_disposition&&(
                  <div style={{marginTop:10,display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
                    {lead.last_disposition&&<Badge color="gray">Last: {lead.last_disposition}</Badge>}
                    {lead.last_remark&&<span style={{opacity:.7,fontSize:12}}>"{lead.last_remark}"</span>}
                  </div>
                )}
                <div style={{display:'flex',gap:10,marginTop:14}}>
                  {lead.script&&<button onClick={()=>setShowScript(!showScript)} style={{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.2)',borderRadius:6,padding:'6px 14px',color:'#fff',fontSize:12,cursor:'pointer'}}>
                    {showScript?'Hide Script':'Show Script'}
                  </button>}
                </div>
                {showScript&&<div className="script" style={{marginTop:14}}>{lead.script}</div>}
              </div>

              {/* Disposition */}
              <Card>
                <CT icon="📝">Disposition</CT>
                <div className="disp-grid">
                  {dispConfig.map(d=>(
                    <button key={d.code} className={`disp-btn${disp===d.code?` ${d.cls||'dsel-green'}`:''}`} onClick={()=>setDisp(d.code)}>{d.label}</button>
                  ))}
                  <button className={`disp-btn${showPunch?'dsel-orange':''}`} onClick={()=>{
                    setPunchForm(f=>({...f,phone:lead?.phone||'',customer_name:lead?.name||'',product_id:''}));
                    setShowPunch(true);
                  }} style={{background:showPunch?'rgba(255,107,43,0.15)':'',borderColor:showPunch?'var(--orange)':'',color:showPunch?'var(--orange)':''}}>📋 Punch Order</button>
                </div>
                {disp==='FU'&&<Inp label="Follow Up Date & Time" type="datetime-local" value={followupAt} onChange={e=>setFollowupAt(e.target.value)}/>}
                <TA label="Remark (optional)" value={remark} onChange={e=>setRemark(e.target.value)} rows={2} placeholder="Any notes about this call..."/>
                <Btn color="green" full onClick={submit} disabled={submitting||!disp}>{submitting?'Saving...':'Submit & Next Lead'}</Btn>
              </Card>

              {/* Call History */}
              {lead.history?.length>0&&(
                <Card>
                  <CT icon="📜">Call History</CT>
                  {lead.history.map((h,i)=>(
                    <div key={i} className="hist-item">
                      <span className="hist-time">{h.attempted_at?.slice(0,16)}</span>
                      <Badge color={h.disposition==='CONV'?'green':h.disposition==='FU'?'orange':'gray'}>{h.disposition}</Badge>
                      {h.remark&&<span style={{color:'var(--text2)',fontSize:13}}>{h.remark}</span>}
                    </div>
                  ))}
                </Card>
              )}
            </>
          )}
        </div>
      </div>
      {/* Punch Order Modal */}
      <div className={`modal-bg${showPunch?' open':''}`} onClick={e=>e.target===e.currentTarget&&setShowPunch(false)}>
        <div className="modal">
          <div className="modal-title">📋 Punch Order</div>
          <div className="g2">
            <Inp label="Customer Name" value={punchForm.customer_name} onChange={e=>setPunchForm({...punchForm,customer_name:e.target.value})} placeholder="Full name"/>
            <Inp label="Phone Number" value={punchForm.phone} onChange={e=>setPunchForm({...punchForm,phone:e.target.value})}/>
          </div>
          <TA label="Full Address (Optional)" value={punchForm.address} onChange={e=>setPunchForm({...punchForm,address:e.target.value})} rows={2} placeholder="House no, Street, Area..."/>
          <div className="g2">
            <Inp label="City" value={punchForm.city} onChange={e=>setPunchForm({...punchForm,city:e.target.value})}/>
            <Sel label="Product" value={punchForm.product_id} onChange={e=>setPunchForm({...punchForm,product_id:e.target.value})}>
              <option value="">-- Select --</option>
              {products.map(p=><option key={p.id} value={p.id}>{p.name} (Min ₹{p.min_price})</option>)}
            </Sel>
          </div>
          <div className="g2">
            <Inp label="Quantity" type="number" value={punchForm.quantity} onChange={e=>setPunchForm({...punchForm,quantity:e.target.value})} min={1}/>
            <Inp label="Price (₹)" type="number" value={punchForm.price} onChange={e=>setPunchForm({...punchForm,price:e.target.value})} placeholder="Selling price"/>
          </div>
          <TA label="Remark (optional)" value={punchForm.remark} onChange={e=>setPunchForm({...punchForm,remark:e.target.value})} rows={2}/>
          <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:16}}>
            <Btn color="ghost" onClick={()=>setShowPunch(false)}>Cancel</Btn>
            <Btn color="orange" onClick={submitPunch} disabled={punching}>{punching?'Submitting...':'Submit Punch Order'}</Btn>
          </div>
        </div>
      </div>
    </>
  );
}

// ============================================================
// CALLER - MY PUNCHES
// ============================================================
function MyPunches({ user }) {
  const [punches, setPunches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCustom, setShowCustom] = useState(false);
  const [customForm, setCustomForm] = useState({customer_name:'',phone:'',address:'',city:'',product_id:'',quantity:1,price:'',remark:''});
  const [products, setProducts] = useState([]);
  const [punching, setPunching] = useState(false);

  const load = async()=>{
    setLoading(true);
    const r=await api('punch.php?action=my_punches');
    if(r.status==='success') setPunches(r.data);
    setLoading(false);
  };
  useEffect(()=>{
    load();
    api('misc.php?type=products&action=list').then(r=>{if(r.status==='success')setProducts(r.data);});
  },[]);

  const submitCustom = async()=>{
    if(!customForm.customer_name||!customForm.phone||!customForm.city||!customForm.product_id||!customForm.price)
      return toast('Name, phone, city, product and price required','error');
    setPunching(true);
    const r=await api('punch.php?action=custom','POST',customForm);
    if(r.status==='success'){toast(r.message);setShowCustom(false);setCustomForm({customer_name:'',phone:'',address:'',city:'',product_id:'',quantity:1,price:'',remark:''});load();}
    else toast(r.message,'error');
    setPunching(false);
  };

  const repunch = async(id)=>{
    const r=await api('punch.php?action=repunch','POST',{punch_id:id});
    if(r.status==='success'){toast('Repunched to Google Sheet!');load();}
    else toast(r.message,'error');
  };

  const cancel = async(id)=>{
    if(!window.confirm('Cancel this punch order?')) return;
    const r=await api('punch.php?action=cancel','POST',{punch_id:id});
    if(r.status==='success'){toast('Punch cancelled','info');load();}
    else toast(r.message,'error');
  };

  const markConv = async(punch)=>{
    const r=await api('leads.php?action=mark_converted','POST',{lead_id:punch.lead_id});
    if(r.status==='success'){toast('Marked as converted!');load();}
    else toast(r.message,'error');
  };

  const statusColor = {pending:'orange',synced:'blue',cancelled:'gray',converted:'green'};

  return (
    <>
    <div style={{display:'flex',justifyContent:'flex-end',marginBottom:12}}>
      <Btn color="orange" onClick={()=>setShowCustom(true)}>📋 Custom/Verbal Punch</Btn>
    </div>
    <Card>
      <CT icon="📋">My Punch Orders</CT>
      {loading?<Spin/>:punches.length===0?<Empty title="No punch orders yet"/>:(
        punches.map(p=>(
          <div key={p.id} style={{padding:'16px 0',borderBottom:'1px solid var(--card-border)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
              <div>
                <div style={{fontWeight:700,fontSize:15}}>{p.customer_name}</div>
                <div style={{fontFamily:'var(--mono)',color:'var(--green)',fontWeight:700}}>{p.phone}</div>
                <div style={{color:'var(--text3)',fontSize:12,marginTop:2}}>{p.product_name} · Qty: {p.quantity} · ₹{p.price}</div>
                <div style={{color:'var(--text3)',fontSize:12}}>{p.city} — {p.address?.slice(0,50)}{p.address?.length>50?'...':''}</div>
              </div>
              <div style={{textAlign:'right'}}>
                <Badge color={statusColor[p.status]}>{p.status}</Badge>
                {p.sheet_synced&&<div style={{color:'var(--green)',fontSize:11,marginTop:4}}>✅ Sheet synced</div>}
                {!p.sheet_synced&&p.status!=='cancelled'&&<div style={{color:'var(--orange)',fontSize:11,marginTop:4}}>⚠️ Sheet sync pending</div>}
                <div style={{color:'var(--text3)',fontSize:11,marginTop:4,fontFamily:'var(--mono)'}}>{p.created_at?.slice(0,16)}</div>
              </div>
            </div>
            <div style={{display:'flex',gap:8}}>
              {!p.sheet_synced&&p.status==='pending'&&<Btn size="sm" color="orange" onClick={()=>repunch(p.id)}>↻ Repunch</Btn>}
              {p.status==='synced'&&<Btn size="sm" color="green" onClick={()=>markConv(p)}>✅ Mark Converted</Btn>}
              {['pending','synced'].includes(p.status)&&<Btn size="sm" color="red" onClick={()=>cancel(p.id)}>Cancel</Btn>}
            </div>
          </div>
        ))
      )}
    </Card>

    {/* Custom Punch Modal */}
    <div className={`modal-bg${showCustom?' open':''}`} onClick={e=>e.target===e.currentTarget&&setShowCustom(false)}>
      <div className="modal">
        <div className="modal-title">📋 Custom / Verbal Punch</div>
        <p style={{color:'var(--text3)',fontSize:13,marginBottom:16}}>Bina CRM lead ke — verbally ya bahar se aaya order punch karo.</p>
        <div className="g2">
          <Inp label="Customer Name" value={customForm.customer_name} onChange={e=>setCustomForm({...customForm,customer_name:e.target.value})}/>
          <Inp label="Phone" value={customForm.phone} onChange={e=>setCustomForm({...customForm,phone:e.target.value})}/>
        </div>
        <TA label="Address (Optional)" value={customForm.address} onChange={e=>setCustomForm({...customForm,address:e.target.value})} rows={2}/>
        <div className="g2">
          <Inp label="City" value={customForm.city} onChange={e=>setCustomForm({...customForm,city:e.target.value})}/>
          <Sel label="Product" value={customForm.product_id} onChange={e=>setCustomForm({...customForm,product_id:e.target.value})}>
            <option value="">-- Select --</option>
            {products.map(p=><option key={p.id} value={p.id}>{p.name} (Min ₹{p.min_price})</option>)}
          </Sel>
        </div>
        <div className="g2">
          <Inp label="Quantity" type="number" value={customForm.quantity} onChange={e=>setCustomForm({...customForm,quantity:e.target.value})} min={1}/>
          <Inp label="Price (₹)" type="number" value={customForm.price} onChange={e=>setCustomForm({...customForm,price:e.target.value})}/>
        </div>
        <TA label="Remark (Optional)" value={customForm.remark} onChange={e=>setCustomForm({...customForm,remark:e.target.value})} rows={2}/>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:16}}>
          <Btn color="ghost" onClick={()=>setShowCustom(false)}>Cancel</Btn>
          <Btn color="orange" onClick={submitCustom} disabled={punching}>{punching?'Submitting...':'Submit Custom Punch'}</Btn>
        </div>
      </div>
    </div>
    </>
  );
}

// ============================================================
// MY LEADS (Caller)
// ============================================================
function MyLeads() {
  const [leads, setLeads] = useState([]);
  const [pool, setPool] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [punchLead, setPunchLead] = useState(null);
  const [remarkLead, setRemarkLead] = useState(null);
  const [punchForm, setPunchForm] = useState({customer_name:'',phone:'',address:'',city:'',product_id:'',quantity:1,price:'',remark:''});
  const [remarkForm, setRemarkForm] = useState({disp:'',remark:'',followup_at:''});
  const [products, setProducts] = useState([]);
  const [punching, setPunching] = useState(false);
  const [remarking, setRemarking] = useState(false);

  const load = useCallback(async()=>{
    setLoading(true);
    const r=await api(`leads.php?action=my_leads&pool=${pool}&search=${encodeURIComponent(search)}`);
    if(r.status==='success') setLeads(r.data);
    setLoading(false);
  },[pool,search]);

  useEffect(()=>{load();},[load]);
  useEffect(()=>{ api('misc.php?type=products&action=list').then(r=>{if(r.status==='success')setProducts(r.data);}); },[]);

  const openPunch = (l)=>{ setPunchLead(l); setPunchForm({customer_name:l.name||'',phone:l.phone||'',address:'',city:'',product_id:'',quantity:1,price:'',remark:''}); };
  const openRemark = (l)=>{ setRemarkLead(l); setRemarkForm({disp:'',remark:'',followup_at:''}); };

  const submitPunch = async()=>{
    if(!punchForm.customer_name||!punchForm.phone||!punchForm.city||!punchForm.product_id||!punchForm.price)
      return toast('Name, phone, city, product and price required','error');
    setPunching(true);
    const r=await api('punch.php?action=create','POST',{...punchForm,lead_id:punchLead.id});
    if(r.status==='success'){toast(r.message);setPunchLead(null);load();}
    else toast(r.message,'error');
    setPunching(false);
  };

  const submitRemark = async()=>{
    if(!remarkForm.disp) return toast('Select a disposition','error');
    if(remarkForm.disp==='FU'&&!remarkForm.followup_at) return toast('Follow up date/time required','error');
    setRemarking(true);
    const r=await api('leads.php?action=dispose','POST',{lead_id:remarkLead.id,disposition:remarkForm.disp,remark:remarkForm.remark,followup_at:remarkForm.followup_at||null});
    if(r.status==='success'){toast('Remark saved!');setRemarkLead(null);load();}
    else toast(r.message,'error');
    setRemarking(false);
  };

  const dispConfig = [
    {code:'NC',label:'Not Connected'},{code:'SW',label:'Switch Off'},{code:'BZ',label:'Busy'},
    {code:'NP',label:'Not Picked'},{code:'OFN',label:'Out of Network'},{code:'CB',label:'Call Back'},
    {code:'NI',label:'Not Interested'},{code:'FU',label:'Follow Up'},{code:'CUSTOM',label:'Custom'},
  ];

  const poolColor = {fresh:'green',recent:'orange',followup:'yellow',converted:'blue'};
  const dispColor = {CONV:'green',FU:'orange',NI:'red',PUNCH:'blue'};

  return (
    <>
    <Card>
      <CT icon="📋">My Leads</CT>
      <div className="tabs">
        {['all','fresh','recent','followup','converted'].map(p=>(
          <button key={p} className={`tab${pool===p?' active':''}`} onClick={()=>setPool(p)} style={{textTransform:'capitalize'}}>{p==='all'?'All':p}</button>
        ))}
      </div>
      <div className="search-bar" style={{marginBottom:14}}>
        <span>🔍</span>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name or number..."/>
      </div>
      {loading?<Spin/>:leads.length===0?<Empty title="No leads found"/>:(
        <div style={{overflowX:'auto'}}>
          <table className="tbl">
            <thead><tr><th>Name</th><th>Phone</th><th>Product</th><th>Pool</th><th>Last Status</th><th>Remark</th><th>Actions</th></tr></thead>
            <tbody>
              {leads.map(l=>(
                <tr key={l.id}>
                  <td style={{fontWeight:600}}>{l.name||'—'}</td>
                  <td style={{fontFamily:'var(--mono)',color:'var(--green)',fontWeight:700}}>{l.phone}</td>
                  <td><Badge color="blue">{l.product_name}</Badge></td>
                  <td><Badge color={poolColor[l.pool]||'gray'}>{l.pool}</Badge></td>
                  <td>{l.last_disposition?<Badge color={dispColor[l.last_disposition]||'gray'}>{l.last_disposition}</Badge>:'—'}</td>
                  <td style={{color:'var(--text3)',fontSize:12,maxWidth:150}}>{l.last_remark||'—'}</td>
                  <td>
                    {(l.pool==='recent'||l.pool==='followup')&&(
                      <div style={{display:'flex',gap:6}}>
                        <Btn size="sm" color="orange" onClick={()=>openPunch(l)}>Punch</Btn>
                        <Btn size="sm" color="ghost" onClick={()=>openRemark(l)}>Remark</Btn>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>

    {/* PUNCH MODAL */}
    <div className={`modal-bg${punchLead?' open':''}`} onClick={e=>e.target===e.currentTarget&&setPunchLead(null)}>
      <div className="modal">
        <div className="modal-title">📋 Punch Order — {punchLead?.name||punchLead?.phone}</div>
        <div className="g2">
          <Inp label="Customer Name" value={punchForm.customer_name} onChange={e=>setPunchForm({...punchForm,customer_name:e.target.value})}/>
          <Inp label="Phone" value={punchForm.phone} onChange={e=>setPunchForm({...punchForm,phone:e.target.value})}/>
        </div>
        <TA label="Address (Optional)" value={punchForm.address} onChange={e=>setPunchForm({...punchForm,address:e.target.value})} rows={2}/>
        <div className="g2">
          <Inp label="City" value={punchForm.city} onChange={e=>setPunchForm({...punchForm,city:e.target.value})}/>
          <Sel label="Product" value={punchForm.product_id} onChange={e=>setPunchForm({...punchForm,product_id:e.target.value})}>
            <option value="">-- Select --</option>
            {products.map(p=><option key={p.id} value={p.id}>{p.name} (Min ₹{p.min_price})</option>)}
          </Sel>
        </div>
        <div className="g2">
          <Inp label="Quantity" type="number" value={punchForm.quantity} onChange={e=>setPunchForm({...punchForm,quantity:e.target.value})} min={1}/>
          <Inp label="Price (₹)" type="number" value={punchForm.price} onChange={e=>setPunchForm({...punchForm,price:e.target.value})}/>
        </div>
        <TA label="Remark (Optional)" value={punchForm.remark} onChange={e=>setPunchForm({...punchForm,remark:e.target.value})} rows={2}/>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:16}}>
          <Btn color="ghost" onClick={()=>setPunchLead(null)}>Cancel</Btn>
          <Btn color="orange" onClick={submitPunch} disabled={punching}>{punching?'Submitting...':'Submit Punch'}</Btn>
        </div>
      </div>
    </div>

    {/* REMARK MODAL */}
    <div className={`modal-bg${remarkLead?' open':''}`} onClick={e=>e.target===e.currentTarget&&setRemarkLead(null)}>
      <div className="modal">
        <div className="modal-title">✏️ Remark — {remarkLead?.name||remarkLead?.phone}</div>
        <div style={{fontFamily:'var(--mono)',color:'var(--green)',fontWeight:700,fontSize:18,marginBottom:16}}>{remarkLead?.phone}</div>
        <div className="flabel" style={{marginBottom:8}}>Disposition</div>
        <div className="disp-grid" style={{marginBottom:16}}>
          {dispConfig.map(d=>(
            <button key={d.code} className={`disp-btn${remarkForm.disp===d.code?' dsel-green':''}`} onClick={()=>setRemarkForm({...remarkForm,disp:d.code})}>{d.label}</button>
          ))}
        </div>
        {remarkForm.disp==='FU'&&<Inp label="Follow Up Date & Time" type="datetime-local" value={remarkForm.followup_at} onChange={e=>setRemarkForm({...remarkForm,followup_at:e.target.value})}/>}
        <TA label="Remark" value={remarkForm.remark} onChange={e=>setRemarkForm({...remarkForm,remark:e.target.value})} rows={3} placeholder="What happened on this call..."/>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:16}}>
          <Btn color="ghost" onClick={()=>setRemarkLead(null)}>Cancel</Btn>
          <Btn color="green" onClick={submitRemark} disabled={remarking||!remarkForm.disp}>{remarking?'Saving...':'Save Remark'}</Btn>
        </div>
      </div>
    </div>
    </>
  );
}

// ============================================================
// CALLER - PERFORMANCE
// ============================================================
function Performance() {
  const [data, setData] = useState(null);
  useEffect(()=>{ api('reports.php?action=personal').then(r=>{if(r.status==='success')setData(r.data);}); },[]);
  if(!data) return <Spin/>;
  const t = data.today||{};
  const target = data.daily_target||5;
  const progress = Math.min(100,Math.round((t.converted||0)/target*100));

  return (
    <>
      <div className="stat-grid">
        <StatCard val={t.converted||0} label="Today Converted" icon="✅" bg="rgba(0,200,83,0.15)" ac="linear-gradient(90deg,#00c853,#69f0ae)"/>
        <StatCard val={target} label="Daily Target" icon="🎯" bg="rgba(255,107,43,0.15)" ac="linear-gradient(90deg,#ff6b2b,#ff9a3c)"/>
        <StatCard val={t.total_attempts||0} label="Total Attempts" icon="📞" bg="rgba(33,150,243,0.15)" ac="linear-gradient(90deg,#2196f3,#42a5f5)"/>
        <StatCard val={`${data.streak||0}d`} label="Current Streak" icon="🔥" bg="rgba(156,39,176,0.15)" ac="linear-gradient(90deg,#9c27b0,#ba68c8)"/>
      </div>
      <Card>
        <CT icon="🎯">Daily Target Progress</CT>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}>
          <span style={{color:'var(--text2)',fontWeight:500}}>Progress</span>
          <span style={{fontWeight:800,color:progress>=100?'var(--green)':progress>=60?'var(--yellow)':'var(--red)'}}>{t.converted||0}/{target} ({progress}%)</span>
        </div>
        <div className="progress-wrap" style={{height:16}}>
          <div className="progress-bar" style={{width:`${progress}%`,background:progress>=100?'var(--green)':progress>=60?'var(--yellow)':'var(--red)'}}/>
        </div>
        {progress>=100&&<div style={{marginTop:12,textAlign:'center',color:'var(--green)',fontWeight:700}}>🎉 Target achieved today!</div>}
      </Card>
      <Card>
        <CT icon="📈">Weekly Performance</CT>
        {(data.weekly||[]).length?(
          <div style={{display:'flex',alignItems:'flex-end',gap:8,height:130,padding:'0 4px'}}>
            {data.weekly.map((w,i)=>{
              const max=Math.max(...data.weekly.map(x=>parseInt(x.converted)||0),1);
              const h=Math.max(6,Math.round((parseInt(w.converted)||0)/max*110));
              return(
                <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                  <span style={{fontSize:11,fontWeight:700,color:'var(--green)'}}>{w.converted}</span>
                  <div style={{width:'100%',height:`${h}px`,background:'linear-gradient(0deg,var(--green),var(--green2))',borderRadius:'4px 4px 0 0'}}/>
                  <span style={{fontSize:10,color:'var(--text3)'}}>{w.date?.slice(5)}</span>
                </div>
              );
            })}
          </div>
        ):<Empty title="No data yet"/>}
      </Card>
    </>
  );
}

// ============================================================
// LEADERBOARD
// ============================================================
function Leaderboard({ user }) {
  const [board, setBoard] = useState([]);
  useEffect(()=>{ api('reports.php?action=leaderboard').then(r=>{if(r.status==='success')setBoard(r.data);}); },[]);
  const medals = ['🥇','🥈','🥉'];
  return (
    <Card>
      <CT icon="🏆">Leaderboard — Today</CT>
      {board.map((b,i)=>(
        <div key={b.id} className={`lb-row${b.is_me?' lb-me':''}`}>
          <span style={{fontSize:22,width:36,textAlign:'center'}}>{medals[i]||`#${i+1}`}</span>
          <div style={{flex:1}}>
            <div style={{fontWeight:700}}>{b.name}{b.is_me&&<Badge color="green" style={{marginLeft:8}}>You</Badge>}</div>
            <div style={{color:'var(--text3)',fontSize:12,marginTop:2}}>Total: {b.total_conv} conversions</div>
          </div>
          <div style={{textAlign:'right'}}>
            <div style={{fontSize:26,fontWeight:800,color:'var(--green)',fontFamily:'Poppins,sans-serif'}}>{b.today_conv}</div>
            <div style={{color:'var(--text3)',fontSize:11}}>Today</div>
          </div>
        </div>
      ))}
      {!board.length&&<Empty title="No data yet"/>}
    </Card>
  );
}

// ============================================================
// SEARCH LEAD
// ============================================================
function SearchLead() {
  const [phone, setPhone] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = async()=>{
    if(!phone.trim()) return;
    setLoading(true);setSearched(true);
    const r=await api(`leads.php?action=search&phone=${encodeURIComponent(phone)}`);
    if(r.status==='success') setResults(r.data);
    setLoading(false);
  };

  const poolColor = {fresh:'green',recent:'orange',followup:'yellow',converted:'blue'};

  return (
    <Card>
      <CT icon="🔍">Search Lead — Inbound Call</CT>
      <div style={{display:'flex',gap:10,marginBottom:20}}>
        <input className="inp" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="Enter customer phone number..." style={{flex:1,fontSize:16,letterSpacing:1}} onKeyDown={e=>e.key==='Enter'&&search()}/>
        <Btn color="green" onClick={search} disabled={loading}>Search</Btn>
      </div>
      {loading?<Spin/>:searched&&results.length===0?<Empty title="No lead found" desc="This number has no record in CRM"/>:
        results.map(l=>(
          <div key={l.id} style={{padding:18,background:'var(--bg2)',border:'1px solid var(--card-border)',borderRadius:'var(--r)',marginBottom:12}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <div>
                <div style={{fontWeight:700,fontSize:15}}>{l.name||'Name not available'}</div>
                <div style={{fontFamily:'var(--mono)',color:'var(--green)',fontWeight:700,fontSize:20,margin:'6px 0'}}>{l.phone}</div>
                <div style={{color:'var(--text3)',fontSize:12}}>{l.product_name}</div>
                {l.caller_name&&<div style={{color:'var(--text3)',fontSize:12}}>Assigned to: {l.caller_name}</div>}
                {l.last_remark&&<div style={{color:'var(--text2)',fontSize:13,marginTop:6,fontStyle:'italic'}}>Last remark: "{l.last_remark}"</div>}
              </div>
              <div style={{textAlign:'right',display:'flex',flexDirection:'column',gap:6,alignItems:'flex-end'}}>
                <Badge color={poolColor[l.pool]||'gray'}>{l.pool}</Badge>
                {l.last_disposition&&<Badge color="gray">{l.last_disposition}</Badge>}
                <span style={{color:'var(--text3)',fontSize:12}}>Status: {l.pool}</span>
              </div>
            </div>
          </div>
        ))
      }
    </Card>
  );
}

// ============================================================
// PROFILE
// ============================================================
function Profile({ user }) {
  const [form, setForm] = useState({ name:user.name||'', email:user.email||'', current_password:'', new_password:'', confirm_password:'' });
  const [note, setNote] = useState('');
  const [noteSaved, setNoteSaved] = useState(false);

  useEffect(()=>{
    const saved = localStorage.getItem(`ev_note_${user.id}`)||'';
    setNote(saved);
  },[user.id]);

  const saveNote = ()=>{
    localStorage.setItem(`ev_note_${user.id}`, note);
    setNoteSaved(true);
    toast('Note saved!');
    setTimeout(()=>setNoteSaved(false),2000);
  };

  const saveProfile = async()=>{
    const r=await api('users.php?action=update','PUT',{id:user.id,name:form.name,email:form.email});
    if(r.status==='success') toast('Profile updated!');
    else toast(r.message,'error');
  };

  const changePass = async()=>{
    if(!form.new_password) return toast('Enter new password','error');
    if(form.new_password!==form.confirm_password) return toast('Passwords do not match','error');
    if(form.new_password.length<6) return toast('Password must be at least 6 characters','error');
    const r=await api('auth.php?action=change_password','POST',{new_password:form.new_password});
    if(r.status==='success'){toast('Password changed!');setForm(f=>({...f,new_password:'',confirm_password:''}));}
    else toast(r.message,'error');
  };

  return (
    <>
    <div className="g2">
      <Card>
        <CT icon="👤">Profile Information</CT>
        <div style={{textAlign:'center',marginBottom:20}}>
          <div style={{width:70,height:70,borderRadius:'50%',background:'linear-gradient(135deg,var(--green),var(--green2))',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:28,fontWeight:800,margin:'0 auto 12px'}}>{user.name[0].toUpperCase()}</div>
          <Badge color={user.role==='admin'?'purple':user.role==='tl'?'blue':'gray'} style={{textTransform:'capitalize'}}>{user.role}</Badge>
        </div>
        <Inp label="Full Name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/>
        <Inp label="Email" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/>
        <Btn color="green" full onClick={saveProfile}>Save Profile</Btn>
      </Card>
      <Card>
        <CT icon="🔒">Change Password</CT>
        <Inp label="New Password" type="password" value={form.new_password} onChange={e=>setForm({...form,new_password:e.target.value})} placeholder="Min 6 characters"/>
        <Inp label="Confirm New Password" type="password" value={form.confirm_password} onChange={e=>setForm({...form,confirm_password:e.target.value})} placeholder="Repeat password"/>
        <Btn color="orange" full onClick={changePass}>Change Password</Btn>
      </Card>
    </div>
    <Card style={{marginTop:0}}>
      <CT icon="📓">My Personal Notes</CT>
      <p style={{color:'var(--text3)',fontSize:13,marginBottom:12}}>Private notes — only you can see this. Jot down anything useful.</p>
      <textarea className="ta" value={note} onChange={e=>setNote(e.target.value)} rows={8} placeholder="Write your personal notes here...&#10;- Customer callbacks&#10;- Scripts that worked&#10;- Daily reminders&#10;- Anything you want to remember..." style={{fontFamily:'var(--mono)',fontSize:13,lineHeight:1.8}}/>
      <div style={{display:'flex',justifyContent:'flex-end',marginTop:10}}>
        <Btn color="green" onClick={saveNote}>{noteSaved?'✅ Saved!':'💾 Save Notes'}</Btn>
      </div>
    </Card>
    </>
  );
}

// ============================================================
// TL ROLL RECENT DATA
// ============================================================
function TLRollRecent({ products }) {
  const [productId, setProductId] = useState('');
  const [result, setResult] = useState(null);

  const roll = async()=>{
    const r=await api('leads.php?action=roll_recent','POST',{product_id:productId||null});
    if(r.status==='success'){setResult(r.data);toast(`${r.data.rolled} leads rolled back to your pool!`);}
    else toast(r.message,'error');
  };

  return null; // This is integrated into TL data page
}

// ============================================================
// FOLLOW UP FLASHER
// ============================================================
function FUFlasher({ onNav }) {
  const [urgent, setUrgent] = useState([]);
  const [flash, setFlash] = useState(false);

  useEffect(()=>{
    const check = async()=>{
      const r = await api('leads.php?action=followups');
      if(r.status==='success'){
        const now = new Date();
        const soon = r.data.filter(l=>{
          const due = new Date(l.followup_at);
          const diff = (due - now) / 60000; // minutes
          return diff <= 5 && diff >= -30; // due in 5 min or overdue up to 30 min
        });
        setUrgent(soon);
      }
    };
    check();
    const t = setInterval(check, 30000);
    return ()=>clearInterval(t);
  },[]);

  useEffect(()=>{
    if(urgent.length>0){
      const t = setInterval(()=>setFlash(f=>!f), 800);
      return ()=>clearInterval(t);
    } else setFlash(false);
  },[urgent]);

  if(urgent.length===0) return null;

  return (
    <div onClick={()=>onNav('followups')} style={{
      cursor:'pointer',
      background: flash ? 'var(--red)' : 'rgba(244,67,54,0.15)',
      border:`2px solid var(--red)`,
      borderRadius:8,
      padding:'6px 14px',
      display:'flex',
      alignItems:'center',
      gap:8,
      transition:'background .3s',
      animation:'none'
    }}>
      <span style={{fontSize:16}}>📅</span>
      <span style={{fontWeight:700,fontSize:13,color: flash?'#fff':'var(--red)'}}>
        {urgent.length} Follow Up{urgent.length>1?'s':''} Due!
      </span>
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState('');
  const [dark, setDark] = useState(false);
  const [products, setProducts] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [approvalStatus, setApprovalStatus] = useState('approved');
  const approvalTimer = useRef(null);

  const startApprovalPoll = useCallback(()=>{
    if(approvalTimer.current) clearInterval(approvalTimer.current);
    approvalTimer.current = setInterval(async()=>{
      const r = await api('auth.php?action=check_approval');
      if(r.status==='success' && r.data && r.data.status){
        const st = r.data.status;
        if(st==='pending') return; // still waiting, do nothing
        setApprovalStatus(st);
        clearInterval(approvalTimer.current);
        if(st==='denied'||st==='expired'){
          setTimeout(()=>{
            localStorage.removeItem('ev_token');
            setUser(null); setPage(''); setApprovalStatus('approved');
          }, 2500);
        }
        // approved → blur disappears automatically
      }
    }, 4000); // poll every 4 sec
  },[]);

  useEffect(()=>{
    injectStyles(dark);
  },[dark]);

  useEffect(()=>{
    const token=localStorage.getItem('ev_token');
    if(token){
      api('auth.php?action=me').then(r=>{
        if(r.status==='success'){
          setUser(r.data);
          loadProducts();
          if(r.data.approval_status==='pending') {
            setApprovalStatus('pending');
            startApprovalPoll();
          } else {
            setApprovalStatus(r.data.approval_status||'approved');
          }
        }
        else localStorage.removeItem('ev_token');
        setLoading(false);
      });
    } else setLoading(false);
  },[]);

  const loadProducts = async()=>{
    const r=await api('misc.php?type=products&action=list');
    if(r.status==='success') setProducts(r.data);
  };

  const loadNotifs = useCallback(async()=>{
    if(!user) return;
    const r=await api('reports.php?action=notifications');
    if(r.status==='success'){setNotifs(r.data.notifications||[]);setNotifCount(r.data.total||0);}
  },[user]);

  useEffect(()=>{
    if(user){
      const defaults={admin:'dashboard',tl:'dashboard',caller:'calling'};
      setPage(defaults[user.role]||'calling');
      loadProducts();
    }
  },[user]);

  useEffect(()=>{
    if(user){ loadNotifs(); const t=setInterval(loadNotifs,60000); return()=>clearInterval(t); }
  },[user,loadNotifs]);

  const handleLogin = (u)=>{
    setUser(u);
    loadProducts();
    const defaults={admin:'dashboard',tl:'dashboard',caller:'calling'};
    setPage(defaults[u.role]||'calling');
    if(u.approval_status==='pending'){
      setApprovalStatus('pending');
      startApprovalPoll();
    } else {
      setApprovalStatus('approved');
    }
  };

  const handleLogout = ()=>{
    localStorage.removeItem('ev_token');
    setUser(null);setPage('');
  };

  const PAGE_TITLES = {
    dashboard:'Dashboard',data:'Data Management',assign:'Assignment',accounts:'Accounts',
    products:'Products',blacklist:'Blacklist',reports:'Reports',analytics:'Analytics',
    settings:'Settings',team:'Team Monitor',followups:'Follow Ups',report:'Reports',
    messages:'Messages',callers:'My Callers',calling:'Calling Screen',punch:'My Punches',
    myleads:'My Leads',performance:'Performance',leaderboard:'Leaderboard',
    search:'Search Lead',profile:'Profile',
  };

  const renderPage = ()=>{
    const p={user,products};
    switch(page){
      case 'dashboard':  return user?.role==='admin'?<AdminDashboard/>:<TLDashboard user={user}/>;
      case 'data':       return <DataManagement {...p}/>;
      case 'assign':     return <Assignment {...p}/>;
      case 'accounts':   return <AdminAccounts/>;
      case 'products':   return <AdminProducts/>;
      case 'blacklist':  return <AdminBlacklist/>;
      case 'reports':    return <AdminReports {...p}/>;
      case 'settings':   return <AdminSettings/>;
      case 'team':       return <TLDashboard user={user}/>;
      case 'followups':  return <FollowUps user={user}/>;
      case 'report':     return <TLReport {...p}/>;
      case 'messages':   return <Messages user={user}/>;
      case 'callers':    return <TLCallers user={user}/>;
      case 'calling':    return <CallingScreen user={user}/>;
      case 'punch':      return <MyPunches user={user}/>;
      case 'myleads':    return <MyLeads/>;
      case 'performance':return <Performance/>;
      case 'leaderboard':return <Leaderboard user={user}/>;
      case 'search':     return <SearchLead/>;
      case 'profile':    return <Profile user={user}/>;
      default:           return <div style={{padding:40,color:'var(--text3)'}}>Page not found</div>;
    }
  };

  if(loading) return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#051209'}}>
      <div style={{textAlign:'center'}}>
        <img src={logo} alt="EssenceVeda" style={{height:65,marginBottom:20,opacity:.8}}/>
        <div className="spin" style={{margin:'0 auto',borderTopColor:'#00c853',borderColor:'rgba(0,200,83,0.2)',width:36,height:36}}/>
      </div>
    </div>
  );

  if(!user) return <LoginPage onLogin={handleLogin}/>;

  return (
    <div className="layout">
      <Sidebar user={user} page={page} onNav={setPage} onLogout={handleLogout} notifCount={notifCount}/>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="topbar-title">{PAGE_TITLES[page]||'Dashboard'}</div>
            <div className="topbar-sub">Welcome back, {user.name}</div>
          </div>
          <div className="topbar-right">
            {/* Follow Up Flasher - caller only */}
            {user.role==='caller' && <FUFlasher onNav={setPage}/>}
            <div className="notif-bell" onClick={()=>setShowNotif(!showNotif)}>
              🔔
              {notifCount>0&&<div className="notif-dot"/>}
            </div>
            {showNotif&&(
              <div style={{position:'absolute',top:70,right:80,background:'var(--card)',backdropFilter:'var(--blur)',border:'1px solid var(--card-border)',borderRadius:'var(--r)',padding:16,width:280,boxShadow:'var(--shadow)',zIndex:200}}>
                <div style={{fontWeight:700,marginBottom:12,color:'var(--text)'}}>Notifications</div>
                {notifs.length===0?<div style={{color:'var(--text3)',fontSize:13}}>All caught up! 🎉</div>:
                  notifs.map((n,i)=>(
                    <div key={i} style={{padding:'10px 0',borderBottom:'1px solid var(--card-border)',fontSize:13}}>
                      <span style={{marginRight:8}}>{n.type==='message'?'💬':n.type==='followup'?'📅':n.type==='approval'?'👤':'📋'}</span>
                      {n.message}
                    </div>
                  ))
                }
              </div>
            )}
            <img src={logo} alt="logo" style={{height:34,objectFit:'contain'}}/>
          </div>
        </div>
        <div className="page">{renderPage()}</div>
      </div>
      <div id="_toast" className="_toast"/>
    </div>
  );
}
